import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ChangeEvent, FormEvent, MouseEvent } from "react";
import { useSocket } from "../hooks/useSocket";
import { useMediaDevices } from "../hooks/useMediaDevices";
import { ParticipantsGrid, type Participant } from "./ParticipantsGrid";
import MeetingSummary from "./MeetingSummary";
import soundboardConfig from "../config/soundboard.json";
import soundApplauseMid from "../assets/sound-effects/applause-mid.mp3";
import soundApplauseSingle from "../assets/sound-effects/applause-single.wav";
import soundMassApplause from "../assets/sound-effects/mass-applause.mp3";
import soundCrowdLaugh from "../assets/sound-effects/crowd-laugh.mp3";
import soundMockingLaugh from "../assets/sound-effects/mocking-laugh.wav";
import soundBreakingNews from "../assets/sound-effects/breaking-news.mp3";
import soundCricketChirp from "../assets/sound-effects/cricket-chirp.wav";
import soundCrowdAhh from "../assets/sound-effects/crowd-ahh.wav";
import soundHeavenlyChoir from "../assets/sound-effects/heavenly-choir-singing.mp3";
import soundManAhh from "../assets/sound-effects/man-ahh.wav";
import soundSadTrombone from "../assets/sound-effects/sad-trombone.mp3";
import soundWow from "../assets/sound-effects/wow.wav";
import "./VideoCall.css";

const soundFiles: Record<string, string> = {
  "applause-mid.mp3": soundApplauseMid,
  "applause-single.wav": soundApplauseSingle,
  "mass-applause.mp3": soundMassApplause,
  "crowd-laugh.mp3": soundCrowdLaugh,
  "mocking-laugh.wav": soundMockingLaugh,
  "breaking-news.mp3": soundBreakingNews,
  "cricket-chirp.wav": soundCricketChirp,
  "crowd-ahh.wav": soundCrowdAhh,
  "heavenly-choir-singing.mp3": soundHeavenlyChoir,
  "man-ahh.wav": soundManAhh,
  "sad-trombone.mp3": soundSadTrombone,
  "wow.wav": soundWow,
};

interface SoundboardItem {
  id: string;
  title: string;
  icon: string;
  file: string;
}

interface SoundboardWireMessageV1 {
  v: 1;
  type: "soundboard-play";
  soundId: string;
  fromName: string;
  ts: number;
}

type CallStatus = "idle" | "connecting" | "in-call" | "ended";

interface RemoteParticipant {
  id: string;
  displayName: string;
  avatarUrl?: string;
  lodosUserId?: string;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  screenShareMid?: string | null;
  isHandRaised: boolean;
}

interface ChatAttachment {
  type: string;
  name: string;
  data: string;
  size: number;
}

interface ChatMessage {
  id: string;
  text: string;
  ts: number;
  fromId: string;
  fromName: string;
  fromAvatarUrl?: string;
  isLocal: boolean;
  attachment?: ChatAttachment;
}

interface WorkspaceOption {
  id: string;
  title: string;
}

interface CollaboratorOption {
  id: string;
  name: string;
  email?: string;
}

interface MeMeetInvitation {
  invitationCode: string;
  userID?: string;
  email?: string;
  nameLastname?: string;
}

interface AddChatMessageToChecklistArgs {
  workspaceId: string;
  meetingId: string;
  meetingStartedAt: number;
  message: Pick<ChatMessage, "id" | "text" | "ts" | "fromId" | "fromName">;
}

interface AddChatMessageToChecklistResult {
  checklistId?: string;
  checklistName: string;
}

interface AddChatMessageToNotesArgs {
  meetingId: string;
  meetingStartedAt: number;
  message: Pick<ChatMessage, "id" | "text" | "ts" | "fromId" | "fromName">;
}

interface AddChatMessageToNotesResult {
  noteId?: string;
  noteTitle?: string;
}

interface ChatWireMessageV1 {
  v: 1;
  type: "chat-message";
  id: string;
  ts: number;
  text: string;
  from: {
    name: string;
    avatarUrl?: string;
  };
  attachment?: ChatAttachment;
  meta?: {
    kind: "checklist-added";
    messageId: string;
    workspaceId: string;
    checklistName: string;
    checklistId?: string;
    byUserId?: string;
    byName?: string;
    preview?: string;
  };
}

interface ChatWireChecklistAddedV1 {
  v: 1;
  type: "chat-checklist-added";
  ts: number;
  messageId: string;
  workspaceId: string;
  checklistName: string;
  checklistId?: string;
  byUserId?: string;
  byName?: string;
  preview?: string;
}

type ChatChecklistAddMeta = {
  workspaceId: string;
  checklistId?: string;
  checklistName: string;
  byUserId?: string;
  byName?: string;
  addedAt: number;
  preview?: string;
};

type ChatNotesAddMeta = {
  noteId?: string;
  noteTitle?: string;
  addedAt: number;
};

interface VideoCallProps {
  roomId: string;
  token?: string;
  lodosUserId?: string;
  nickname?: string;
  displayName?: string;
  avatarUrl?: string;
  language?: string;
  changeLanguage?: (lang: string) => void;
  getTranslation?: (key: string, params?: Record<string, string>) => string;
  getShareUrl?: (roomId: string) => string;
  summaryApi?: any;
  getLinkPreview?: (token: string, url: string) => Promise<any>;
  onSendDmOnSocialHive?: (targetUserId: string, displayName: string) => void | Promise<void>;
  workspaces?: WorkspaceOption[];
  onAddChatMessageToChecklist?: (args: AddChatMessageToChecklistArgs) => Promise<AddChatMessageToChecklistResult | undefined>;
  onAddChatMessageToNotes?: (args: AddChatMessageToNotesArgs) => Promise<AddChatMessageToNotesResult | undefined>;
  onGetCollaboratorsService?: () => Promise<any>;
  onInviteUserService?: (options: {
    roomID: string;
    userID?: string;
    email?: string;
    nameLastname?: string;
    // Hangi kanallardan haber verilecegi KULLANICI secimidir.
    // Bos dizi = hicbir sey gonderme (kullanici linki kendi paylasir).
    notify?: string[];
  }) => Promise<any>;
  onIsInvitedUserService?: (options: {
    roomID: string;
    userID?: string;
    email?: string;
  }) => Promise<any>;
  onRemoveInvitationService?: (options: { invitationCode: string }) => Promise<any>;
  onGetInvitationsService?: (options: { roomID: string }) => Promise<any>;
  creatorUserId?: string;
  onCallEnd?: () => void;
  onExitRoom?: () => void;
}

type VideoSenderSlots = {
  cameraSender: RTCRtpSender | null;
  screenSender: RTCRtpSender | null;
};

// Default RTC configuration with STUN and TURN servers
// Default RTC configuration with STUN and TURN servers
const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  {
    urls: "turn:137.184.92.46:3478",
    username: "webrtc",
    credential: "e22a3d7a80236c0dfbac40c5",
  },
  {
    urls: "turn:137.184.92.46:3478?transport=tcp",
    username: "webrtc",
    credential: "e22a3d7a80236c0dfbac40c5",
  },
  {
    urls: "turns:137.184.92.46:5349",
    username: "webrtc",
    credential: "e22a3d7a80236c0dfbac40c5",
  },
];

const DEFAULT_RTC_CONFIG: RTCConfiguration = {
  iceServers: DEFAULT_ICE_SERVERS,
  iceCandidatePoolSize: 10,
};

// Merge server-provided ICE servers with local fallback list, deduped by URL.
// This guarantees at least one working STUN/TURN even if the server returns an
// empty or incomplete list, and keeps the server's fresh credentials as the
// primary candidate.
function mergeIceServers(serverServers: RTCIceServer[]): RTCIceServer[] {
  const normalizeUrls = (urls: string | string[] | undefined): string[] => {
    if (!urls) return [];
    return Array.isArray(urls) ? urls : [urls];
  };

  const seen = new Set<string>();
  const merged: RTCIceServer[] = [];

  const addServer = (server: RTCIceServer) => {
    const urlList = normalizeUrls(server.urls);
    // Dedupe per-URL-entry, keyed on url + credentials so different creds for
    // the same host don't drop each other.
    const credKey = `${server.username ?? ""}:${server.credential ?? ""}`;
    for (const url of urlList) {
      const key = `${url}|${credKey}`;
      if (seen.has(key)) continue;
      seen.add(key);
    }
    merged.push(server);
  };

  for (const s of serverServers) addServer(s);
  for (const s of DEFAULT_ICE_SERVERS) {
    const urlList = normalizeUrls(s.urls);
    const credKey = `${s.username ?? ""}:${s.credential ?? ""}`;
    const alreadyCovered = urlList.every((url) => seen.has(`${url}|${credKey}`));
    if (alreadyCovered) continue;
    addServer(s);
  }

  return merged;
}

const CHAT_CHANNEL_LABEL = "memeet-chat";
const MAX_CHAT_MESSAGES = 200;
const MAX_PREJOIN_PARTICIPANTS = 3;
const MAX_ATTACHMENT_SIZE = 2 * 1024 * 1024; // 2MB
const DEBUG_STORAGE_KEY = "memeet:debug";
const DEBUG_QUERY_KEY = "memeetDebug";
const DEBUG_PREFIX = "[MeMeetDebug]";
const HAND_AUTO_LOWER_SPEAKING_THRESHOLD = 6;
const HAND_AUTO_LOWER_DELAY_MS = 2000;
const HAND_AUTO_LOWER_SILENCE_GRACE_MS = 450;

type NotificationTone = "chat" | "join";

function isMeMeetDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const stored = String(window.localStorage.getItem(DEBUG_STORAGE_KEY) || "").trim().toLowerCase();
    if (stored === "1" || stored === "true" || stored === "on" || stored === "yes") return true;

    const params = new URLSearchParams(window.location.search);
    return params.get(DEBUG_QUERY_KEY) === "1";
  } catch {
    return false;
  }
}

function debugLog(event: string, payload?: Record<string, unknown>) {
  if (!isMeMeetDebugEnabled()) return;
  if (payload) {
    console.log(DEBUG_PREFIX, event, payload);
    return;
  }
  console.log(DEBUG_PREFIX, event);
}

type LinkPreviewState =
  | { title: string; description: string; image: string | null; url: string }
  | { error: true };

const URL_REGEX = /(https?:\/\/[^\s]+|(?:www\.)?[a-z0-9.-]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi;
const URL_REGEX_SINGLE = new RegExp(URL_REGEX.source, "i");

// Keep aligned with SocialHiveChat link detection to avoid false positives.
const VALID_TLDS = [
  ".test",
  ".example",
  ".invalid",
  ".localhost",
  ".dev",
  ".local",
  ".internal",
  ".com",
  ".net",
  ".org",
  ".edu",
  ".gov",
  ".mil",
  ".int",
  ".info",
  ".biz",
  ".name",
  ".pro",
  ".coop",
  ".aero",
  ".museum",
  ".app",
  ".io",
  ".ai",
  ".tech",
  ".online",
  ".site",
  ".website",
  ".store",
  ".shop",
  ".blog",
  ".news",
  ".media",
  ".agency",
  ".company",
  ".digital",
  ".email",
  ".cloud",
  ".network",
  ".systems",
  ".technology",
  ".software",
  ".computer",
  ".codes",
  ".design",
  ".graphics",
  ".photo",
  ".photography",
  ".video",
  ".audio",
  ".music",
  ".film",
  ".studio",
  ".art",
  ".gallery",
  ".zone",
  ".space",
  ".world",
  ".earth",
  ".live",
  ".life",
  ".community",
  ".social",
  ".chat",
  ".link",
  ".click",
  ".wiki",
  ".academy",
  ".university",
  ".school",
  ".education",
  ".institute",
  ".college",
  ".work",
  ".career",
  ".jobs",
  ".services",
  ".solutions",
  ".support",
  ".help",
  ".guide",
  ".tips",
  ".tools",
  ".today",
  ".now",
  ".here",
  ".pub",
  ".cafe",
  ".bar",
  ".restaurant",
  ".pizza",
  ".food",
  ".recipes",
  ".cooking",
  ".kitchen",
  ".menu",
  ".fitness",
  ".health",
  ".medical",
  ".care",
  ".doctor",
  ".dental",
  ".surgery",
  ".clinic",
  ".hospital",
  ".money",
  ".finance",
  ".financial",
  ".bank",
  ".credit",
  ".loan",
  ".tax",
  ".accountant",
  ".trading",
  ".insurance",
  ".fund",
  ".ventures",
  ".capital",
  ".estate",
  ".homes",
  ".house",
  ".property",
  ".realty",
  ".land",
  ".casa",
  ".travel",
  ".flights",
  ".hotels",
  ".vacations",
  ".tours",
  ".cruise",
  ".auto",
  ".car",
  ".cars",
  ".taxi",
  ".bike",
  ".motorcycles",
  ".game",
  ".games",
  ".poker",
  ".casino",
  ".bet",
  ".tennis",
  ".golf",
  ".football",
  ".soccer",
  ".hockey",
  ".baseball",
  ".basketball",
  ".racing",
  ".team",
  ".coach",
  ".fans",
  ".fashion",
  ".clothing",
  ".shoes",
  ".jewelry",
  ".watch",
  ".boutique",
  ".style",
  ".beauty",
  ".makeup",
  ".hair",
  ".skin",
  ".tattoo",
  ".ink",
  ".gifts",
  ".flowers",
  ".toys",
  ".baby",
  ".kids",
  ".family",
  ".wedding",
  ".events",
  ".party",
  ".christmas",
  ".holiday",
  ".us",
  ".uk",
  ".ca",
  ".au",
  ".de",
  ".fr",
  ".it",
  ".es",
  ".nl",
  ".be",
  ".ch",
  ".at",
  ".se",
  ".no",
  ".dk",
  ".fi",
  ".pl",
  ".cz",
  ".ru",
  ".ua",
  ".jp",
  ".cn",
  ".kr",
  ".in",
  ".sg",
  ".hk",
  ".tw",
  ".th",
  ".my",
  ".id",
  ".ph",
  ".vn",
  ".nz",
  ".za",
  ".br",
  ".mx",
  ".ar",
  ".cl",
  ".co",
  ".pe",
  ".ve",
  ".tr",
  ".il",
  ".ae",
  ".sa",
  ".eg",
  ".ng",
  ".ke",
  ".gh",
  ".ma",
  ".tn",
  ".tv",
  ".me",
  ".cc",
  ".ws",
  ".to",
  ".fm",
  ".am",
  ".ly",
  ".gl",
  ".im",
  ".ac",
  ".sh",
  ".tm",
  ".vc",
  ".ag",
  ".bz",
  ".mn",
  ".nu",
  ".sc",
  ".tk",
  ".ga",
  ".cf",
  ".gq",
  ".ml",
];

function hasValidTld(url: string) {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    return VALID_TLDS.some((tld) => hostname.endsWith(tld));
  } catch {
    return false;
  }
}

function normalizeUrl(raw: string): string | null {
  if (!raw) return null;
  let u = String(raw).trim();
  u = u.replace(/[),.;!?]+$/, "");
  if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
  try {
    const url = new URL(u);
    if (!hasValidTld(u)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

function extractUrls(text: string): string[] {
  if (!text || typeof text !== "string") return [];
  const textWithoutEmails = text.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "");
  const found = textWithoutEmails.match(URL_REGEX) || [];
  const normalized = found.map((v) => normalizeUrl(v)).filter(Boolean) as string[];
  return Array.from(new Set(normalized));
}

function getHostName(href: string): string {
  if (!href) return "";
  try {
    const urlObj = new URL(href);
    return urlObj.host.replace(/^www\./i, "") || urlObj.host || href;
  } catch {
    return href;
  }
}

function getResponseData(response: any): any {
  if (!response) return null;
  if (Object.prototype.hasOwnProperty.call(response, "data")) return response.data;
  return response;
}

function readResponseStatus(response: any): boolean {
  const data = getResponseData(response);
  if (typeof data === "boolean") return data;
  if (typeof data?.status === "boolean") return data.status;
  if (typeof response?.status === "boolean") return response.status;
  return false;
}

function normalizeCollaboratorsResponse(response: any): CollaboratorOption[] {
  const data = getResponseData(response);
  if (data && typeof data === "object" && data.status === false) return [];

  const pickArray = (value: any): any[] => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "object") return [];

    const keys = ["result", "collaborators", "members", "items", "rows", "list"];
    for (const key of keys) {
      const next = value[key];
      if (Array.isArray(next)) return next;
      if (next && typeof next === "object") {
        for (const nestedKey of ["collaborators", "members", "items", "rows", "list"]) {
          if (Array.isArray(next[nestedKey])) return next[nestedKey];
        }
      }
    }
    return [];
  };

  const result = pickArray(data);
  const seen = new Set<string>();

  return result
    .map((entry: any) => {
      if (!entry || typeof entry !== "object") return null;
      const rawId = String(
        entry.memberID ??
          entry.targetUserId ??
          entry.collaboratorID ??
          entry.userID ??
          entry.id ??
          ""
      ).trim();
      const email = String(entry.email ?? entry.invitedEmail ?? "").trim() || undefined;
      const name =
        String(
          entry.nameLastname ??
            [entry.name, entry.lastname].filter(Boolean).join(" ") ??
            entry.displayName ??
            entry.fullName ??
            entry.username ??
            entry.email ??
            rawId
        ).trim() || rawId;

      if (!rawId && !email) return null;
      const dedupeKey = rawId || `email:${email}`;
      if (!dedupeKey || seen.has(dedupeKey)) return null;
      seen.add(dedupeKey);

      return {
        id: rawId || `email:${email}`,
        name,
        email,
      } as CollaboratorOption;
    })
    .filter((item: CollaboratorOption | null): item is CollaboratorOption => Boolean(item));
}

function normalizeInvitationsResponse(response: any): MeMeetInvitation[] {
  const data = getResponseData(response);
  const result = Array.isArray(data?.result) ? data.result : Array.isArray(data) ? data : [];
  const seen = new Set<string>();

  return result
    .map((entry: any) => {
      if (!entry || typeof entry !== "object") return null;
      const invitationCode = String(
        entry.invitationCode ??
          entry.invitation_code ??
          entry.code ??
          ""
      ).trim();
      if (!invitationCode || seen.has(invitationCode)) return null;
      seen.add(invitationCode);

      const userID = String(
        entry.userID ??
          entry.invitedUserID ??
          entry.memberID ??
          ""
      ).trim() || undefined;
      const email = String(entry.email ?? entry.invitedEmail ?? "").trim() || undefined;
      const nameLastname =
        String(
          entry.nameLastname ??
            [entry.name, entry.lastname].filter(Boolean).join(" ") ??
            [entry.invitedName, entry.invitedLastname].filter(Boolean).join(" ") ??
            ""
        ).trim() || undefined;

      return {
        invitationCode,
        userID,
        email,
        nameLastname,
      } as MeMeetInvitation;
    })
    .filter((item: MeMeetInvitation | null): item is MeMeetInvitation => Boolean(item));
}

function makeInvitedCheckKey(prefix: string, userID?: string, email?: string): string {
  const normalizedUserID = String(userID || "").trim();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (normalizedUserID) return `${prefix}:u:${normalizedUserID}`;
  if (normalizedEmail) return `${prefix}:e:${normalizedEmail}`;
  return `${prefix}:unknown`;
}

function displayInvitationName(invitation: MeMeetInvitation): string {
  const explicit = String(invitation.nameLastname || "").trim();
  if (explicit) return explicit;
  const fallback = String(invitation.email || invitation.userID || "").trim();
  return fallback || "Unknown";
}

function linkifyText(text: string) {
  const parts = String(text || "").split(URL_REGEX);
  return parts.map((part, i) => {
    if (!part) return null;
    if (URL_REGEX_SINGLE.test(part)) {
      const href = normalizeUrl(part);
      if (!href) return <span key={i}>{part}</span>;
      return (
        <a key={`${i}-${href}`} href={href} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

const PREVIEW_PLACEHOLDER_DATA_URI =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="450" viewBox="0 0 800 450">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#1f2937" stop-opacity="0.55"/>
          <stop offset="1" stop-color="#2563eb" stop-opacity="0.35"/>
        </linearGradient>
      </defs>
      <rect width="800" height="450" fill="url(#g)"/>
      <circle cx="640" cy="120" r="90" fill="#111827" fill-opacity="0.25"/>
      <rect x="80" y="290" width="520" height="26" rx="13" fill="#ffffff" fill-opacity="0.22"/>
      <rect x="80" y="330" width="380" height="20" rx="10" fill="#ffffff" fill-opacity="0.16"/>
    </svg>`
  );

function LinkPreviewSkeleton() {
  return (
    <div className="link-preview-card wa is-loading" aria-hidden="true">
      <div className="wa-thumb">
        <div className="wa-thumb__skeleton" />
      </div>
      <div className="wa-meta">
        <div className="wa-title" />
        <div className="wa-desc" />
        <div className="wa-host" />
      </div>
    </div>
  );
}

function LinkPreviewCard({
  preview,
  href,
  getTranslation,
}: {
  preview: { title: string; description: string; image: string | null; url: string };
  href: string;
  getTranslation?: (key: string, params?: Record<string, string>) => string;
}) {
  const host = getHostName(href);
  const urlObj = (() => {
    try {
      return new URL(href);
    } catch {
      return null;
    }
  })();
  const normalizedTitle = preview?.title?.trim() || href;
  const displayImage = (() => {
    const raw = preview?.image ? String(preview.image) : "";
    if (!raw) return PREVIEW_PLACEHOLDER_DATA_URI;
    if (raw.startsWith("//")) return `https:${raw}`;
    if (/^https?:\/\//i.test(raw)) return raw;
    return normalizeUrl(raw) || raw || PREVIEW_PLACEHOLDER_DATA_URI;
  })();
  const isPlaceholder = !preview?.image;
  const hasDescription = !!(preview?.description && preview.description.trim());
  const hasTitle = !!preview?.title?.trim();
  const defaultDescription = getTranslation ? getTranslation("clickToLearnMore") : "Click to learn more";

  const shouldShowTitleOverlay = hasTitle && hasDescription && isPlaceholder;
  const shouldShowHostOverlay = isPlaceholder && !shouldShowTitleOverlay;

  const origin = urlObj ? `${urlObj.protocol}//${urlObj.host}` : null;
  const primaryFavicon = origin ? `${origin}/favicon.ico` : "";

  return (
    <a className="link-preview-card wa" href={href} target="_blank" rel="noopener noreferrer" title={normalizedTitle}>
      <div className={`wa-thumb ${isPlaceholder ? "wa-thumb--placeholder" : ""}`}>
        <img
          className="wa-thumb__img"
          src={displayImage}
          alt={normalizedTitle}
          loading="lazy"
          onError={(e) => {
            if (e.currentTarget.src !== PREVIEW_PLACEHOLDER_DATA_URI) {
              e.currentTarget.src = PREVIEW_PLACEHOLDER_DATA_URI;
            }
          }}
        />
        {shouldShowHostOverlay && <div className="wa-thumb__placeholder-label">{host}</div>}
        {shouldShowTitleOverlay && (
          <div className="wa-thumb__placeholder-label wa-thumb__placeholder-label--on-image wa-thumb__placeholder-label--title">
            <span>{preview.title}</span>
          </div>
        )}
      </div>

      <div className="wa-meta">
        <div className="wa-title" title={normalizedTitle}>
          {normalizedTitle}
        </div>
        <div className="wa-desc">{hasDescription ? preview.description : defaultDescription}</div>
        <div className="wa-host">
          {primaryFavicon ? <img className="wa-favicon" src={primaryFavicon} alt="" loading="lazy" /> : null}
          <span className="wa-host__text">{host}</span>
        </div>
      </div>
    </a>
  );
}

export function VideoCall({
  roomId,
  token,
  lodosUserId,
  nickname = "User",
  displayName,
  avatarUrl,
  language,
  changeLanguage,
  getTranslation,
  getShareUrl,
  summaryApi,
  getLinkPreview,
  onSendDmOnSocialHive,
  workspaces,
  onAddChatMessageToChecklist,
  onAddChatMessageToNotes,
  onGetCollaboratorsService,
  onInviteUserService,
  onIsInvitedUserService,
  onRemoveInvitationService,
  onGetInvitationsService,
  creatorUserId,
  onCallEnd,
  onExitRoom,
}: VideoCallProps) {
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const [remoteParticipants, setRemoteParticipants] = useState<Map<string, RemoteParticipant>>(new Map());
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isSoundboardOpen, setIsSoundboardOpen] = useState(false);
  const [playingSoundId, setPlayingSoundId] = useState<string | null>(null);
  const soundboardAudioRef = useRef<HTMLAudioElement | null>(null);
  const [chatDraft, setChatDraft] = useState("");
  const [pendingAttachment, setPendingAttachment] = useState<ChatAttachment | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [linkPreviews, setLinkPreviews] = useState<Record<string, LinkPreviewState>>({});
  const fetchingUrlsRef = useRef<Set<string>>(new Set());
  const meetingStartedAtRef = useRef<number>(Date.now());

  const [chatContextMenu, setChatContextMenu] = useState<{
    messageId: string;
    x: number;
    y: number;
  } | null>(null);

  const [isChecklistPickerOpen, setIsChecklistPickerOpen] = useState(false);
  const [checklistPickerMessageId, setChecklistPickerMessageId] = useState<string | null>(null);
  const [hasChecklistWorkspaceSelection, setHasChecklistWorkspaceSelection] = useState<boolean>(() => {
    try {
      if (typeof window === "undefined") return false;
      return window.localStorage.getItem("memeet:hasChecklistWorkspaceSelection") === "1";
    } catch {
      return false;
    }
  });
  const [selectedChecklistWorkspaceId, setSelectedChecklistWorkspaceId] = useState<string>(() => {
    try {
      if (typeof window === "undefined") return "";
      return String(window.localStorage.getItem("memeet:lastChecklistWorkspaceId") || "");
    } catch {
      return "";
    }
  });
  const [checklistActionError, setChecklistActionError] = useState("");
  const [addingChecklistForMessageId, setAddingChecklistForMessageId] = useState<string | null>(null);
  const [isAddingToNotesForMessageId, setIsAddingToNotesForMessageId] = useState<string | null>(null);

  const [checklistAddsByMessageId, setChecklistAddsByMessageId] = useState<Record<string, ChatChecklistAddMeta[]>>({});
  const [notesAddedByMessageId, setNotesAddedByMessageId] = useState<Record<string, ChatNotesAddMeta>>({});

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  // Davet bildirimi kanallari. Bildirim + push varsayilan acik; mail daha
  // araya girici oldugu icin kullanici bilincli acar. Hicbiri secilmezse
  // backend hicbir sey gondermez — davet yine olusur, link kopyalanabilir.
  const [notifyChannels, setNotifyChannels] = useState<{ notification: boolean; push: boolean; mail: boolean }>({
    notification: true,
    push: true,
    mail: false,
  });
  // Davet olustuktan sonra donen katilim baglantisi ("kopyala ve kendim gonder").
  const [inviteJoinLink, setInviteJoinLink] = useState("");
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [isInviteSubmitting, setIsInviteSubmitting] = useState(false);
  const [inviteActionError, setInviteActionError] = useState("");
  const [inviteActionSuccess, setInviteActionSuccess] = useState("");
  const [collaborators, setCollaborators] = useState<CollaboratorOption[]>([]);
  const [isCollaboratorsLoading, setIsCollaboratorsLoading] = useState(false);
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState("");
  const [collaboratorSearchQuery, setCollaboratorSearchQuery] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNameLastname, setInviteNameLastname] = useState("");
  const [invitations, setInvitations] = useState<MeMeetInvitation[]>([]);
  const [isInvitationsLoading, setIsInvitationsLoading] = useState(false);
  const [invitedStatusByKey, setInvitedStatusByKey] = useState<Record<string, boolean>>({});
  const [isCheckingInvitedByKey, setIsCheckingInvitedByKey] = useState<Record<string, boolean>>({});
  const [invitationToRemove, setInvitationToRemove] = useState<MeMeetInvitation | null>(null);
  const [isRemovingInvitation, setIsRemovingInvitation] = useState(false);

  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const videoSenderSlotsRef = useRef<Map<string, VideoSenderSlots>>(new Map());
  const remoteTrackMidToIdRef = useRef<Map<string, Map<string, string>>>(new Map());
  const screenStreamRef = useRef<MediaStream | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const localMediaStateRef = useRef<{ audioEnabled: boolean; videoEnabled: boolean }>({
    audioEnabled: true,
    videoEnabled: true,
  });
  const localHandRaisedRef = useRef(false);
  const remoteParticipantsRef = useRef<Map<string, RemoteParticipant>>(remoteParticipants);
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const pendingUsersRef = useRef<Map<string, RemoteParticipant>>(new Map());
  const iceServersRef = useRef<RTCIceServer[]>([]);
  const localUserIdRef = useRef<string | null>(null);
  const hasStartedPreviewRef = useRef(false);
  const hasSentInitialMediaStateRef = useRef(false);
  const chatChannelsRef = useRef<Map<string, RTCDataChannel>>(new Map());
  const chatMessageIdsRef = useRef<Set<string>>(new Set());
  const chatOpenRef = useRef(false);
  const chatListRef = useRef<HTMLDivElement>(null);
  const chatDraftInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shouldAutoScrollChatRef = useRef(true);
  const hasLoggedDebugSessionRef = useRef(false);
  const autoLowerHandTimeoutRef = useRef<number | null>(null);
  const autoLowerHandSilenceTimeoutRef = useRef<number | null>(null);
  const notificationAudioContextRef = useRef<AudioContext | null>(null);

  const {
    stream: localStream,
    audioLevel,
    isAudioEnabled,
    isVideoEnabled,
    startStream,
    stopStream,
    toggleAudio,
    toggleVideo,
  } = useMediaDevices();

  const effectiveDisplayName = useMemo(
    () => displayName?.trim() || nickname?.trim() || "User",
    [displayName, nickname]
  );

  const encodedNickname = useMemo(
    () => encodeIdentityNickname(effectiveDisplayName, avatarUrl, lodosUserId),
    [effectiveDisplayName, avatarUrl, lodosUserId]
  );

  useEffect(() => {
    if (hasLoggedDebugSessionRef.current) return;
    if (!isMeMeetDebugEnabled()) return;

    debugLog("session-context", {
      roomId,
      tokenPresent: Boolean(token),
      lodosUserId: lodosUserId ? String(lodosUserId) : null,
      effectiveDisplayName,
      encodedNicknameLength: encodedNickname.length,
    });
    hasLoggedDebugSessionRef.current = true;
  }, [roomId, token, lodosUserId, effectiveDisplayName, encodedNickname]);

  const t = useCallback(
    (key: string, fallback: string, params?: Record<string, string>) => {
      if (!getTranslation) return fallback;
      const translated = getTranslation(key, params || {});
      if (!translated || translated === key) return fallback;
      return translated;
    },
    [getTranslation]
  );

  const playNotificationTone = useCallback((tone: NotificationTone) => {
    try {
      if (typeof window === "undefined" || typeof window.AudioContext === "undefined") return;

      if (!notificationAudioContextRef.current || notificationAudioContextRef.current.state === "closed") {
        notificationAudioContextRef.current = new window.AudioContext();
      }

      const audioContext = notificationAudioContextRef.current;
      if (!audioContext) return;

      if (audioContext.state === "suspended") {
        void audioContext.resume().catch(() => undefined);
      }

      const notes =
        tone === "chat"
          ? [
              { frequency: 1046, duration: 0.06 },
              { frequency: 1318, duration: 0.06 },
            ]
          : [{ frequency: 880, duration: 0.09 }];

      let cursor = audioContext.currentTime + 0.01;
      notes.forEach((note) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(note.frequency, cursor);

        gainNode.gain.setValueAtTime(0.0001, cursor);
        gainNode.gain.exponentialRampToValueAtTime(0.12, cursor + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, cursor + note.duration);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.start(cursor);
        oscillator.stop(cursor + note.duration + 0.02);

        cursor += note.duration + 0.04;
      });
    } catch {
      // ignore
    }
  }, []);

  const handChimeCtxRef = useRef<AudioContext | null>(null);
  // El kaldırma bildirim sesi — Web Audio ile nazik iki tonlu "çın" (dosya yok).
  const playHandRaiseChime = useCallback(() => {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      let ctx = handChimeCtxRef.current;
      if (!ctx) { ctx = new Ctx(); handChimeCtxRef.current = ctx; }
      if (ctx!.state === "suspended") void ctx!.resume();
      const now = ctx!.currentTime;
      ([[880, 0], [1175, 0.12]] as [number, number][]).forEach(([freq, offset]) => {
        const osc = ctx!.createOscillator();
        const gain = ctx!.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const t0 = now + offset;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
        osc.connect(gain).connect(ctx!.destination);
        osc.start(t0);
        osc.stop(t0 + 0.24);
      });
    } catch { /* sessiz */ }
  }, []);

  const playSoundboardSound = useCallback((soundId: string) => {
    const soundItem = (soundboardConfig.sounds as SoundboardItem[]).find((s) => s.id === soundId);
    if (!soundItem) return;

    const src = soundFiles[soundItem.file];
    if (!src) return;

    try {
      if (soundboardAudioRef.current) {
        soundboardAudioRef.current.pause();
        soundboardAudioRef.current.currentTime = 0;
      }

      const audio = new Audio(src);
      soundboardAudioRef.current = audio;
      setPlayingSoundId(soundId);

      audio.addEventListener("ended", () => {
        setPlayingSoundId((prev) => (prev === soundId ? null : prev));
      });
      audio.addEventListener("error", () => {
        setPlayingSoundId((prev) => (prev === soundId ? null : prev));
      });

      void audio.play().catch(() => {
        setPlayingSoundId(null);
      });
    } catch {
      setPlayingSoundId(null);
    }
  }, []);

  const broadcastSoundboardPlay = useCallback((soundId: string) => {
    const wireMessage: SoundboardWireMessageV1 = {
      v: 1,
      type: "soundboard-play",
      soundId,
      fromName: effectiveDisplayName,
      ts: Date.now(),
    };

    const payload = JSON.stringify(wireMessage);
    chatChannelsRef.current.forEach((channel) => {
      if (channel.readyState !== "open") return;
      try {
        channel.send(payload);
      } catch {
        // ignore
      }
    });
  }, [effectiveDisplayName]);

  const handleSoundboardClick = useCallback((soundId: string) => {
    playSoundboardSound(soundId);
    broadcastSoundboardPlay(soundId);
  }, [playSoundboardSound, broadcastSoundboardPlay]);

  const toggleSoundboard = useCallback(() => {
    setIsSoundboardOpen((prev) => !prev);
  }, []);

  const resolvedWorkspaces = useMemo(() => {
    const list = Array.isArray(workspaces) ? workspaces : [];
    const normalized = list
      .map((w) => ({
        id: String(w?.id || "").trim(),
        title: String(w?.title || "").trim(),
      }))
      .filter((w) => w.id)
      .map((w) => ({ ...w, title: w.title || w.id }));

    const seen = new Set<string>();
    const deduped = normalized.filter((w) => {
      if (seen.has(w.id)) return false;
      seen.add(w.id);
      return true;
    });

    deduped.sort((a, b) => a.title.localeCompare(b.title));
    return deduped;
  }, [workspaces]);

  const workspaceIdSet = useMemo(() => new Set(resolvedWorkspaces.map((w) => String(w.id))), [resolvedWorkspaces]);

  const canAddToChecklist = resolvedWorkspaces.length > 0 && typeof onAddChatMessageToChecklist === "function";
  const canAddToNotes = typeof onAddChatMessageToNotes === "function";
  const isChecklistAddInFlight = Boolean(addingChecklistForMessageId);
  const isNotesAddInFlight = Boolean(isAddingToNotesForMessageId);
  const canInviteUsers = typeof onInviteUserService === "function";
  const canRemoveInvitations = typeof onRemoveInvitationService === "function";
  const canLoadInvitations = typeof onGetInvitationsService === "function";
  const isRoomCreator = Boolean(creatorUserId) && String(lodosUserId || "") === String(creatorUserId || "");
  const canLoadCollaborators = typeof onGetCollaboratorsService === "function";
  const normalizeCollaboratorSearchText = useCallback(
    (value: string) => String(value || "").toLocaleLowerCase().replace(/\s+/g, " ").trim(),
    []
  );
  const collaboratorSearchTerm = useMemo(
    () => normalizeCollaboratorSearchText(collaboratorSearchQuery),
    [collaboratorSearchQuery, normalizeCollaboratorSearchText]
  );
  const filteredCollaborators = useMemo(() => {
    if (!collaboratorSearchTerm) return collaborators;
    return collaborators.filter((collaborator) => {
      const searchableText = normalizeCollaboratorSearchText(
        `${String(collaborator.name || "")} ${String(collaborator.email || "")}`
      );
      return searchableText.includes(collaboratorSearchTerm);
    });
  }, [collaborators, collaboratorSearchTerm, normalizeCollaboratorSearchText]);

  const fetchCollaborators = useCallback(async (): Promise<CollaboratorOption[]> => {
    if (!onGetCollaboratorsService) {
      setCollaborators([]);
      return [];
    }

    setIsCollaboratorsLoading(true);
    try {
      const response = await onGetCollaboratorsService();
      const next = normalizeCollaboratorsResponse(response).filter(
        (item) => String(item.id || "").trim() !== String(lodosUserId || "").trim()
      );
      setCollaborators(next);
      return next;
    } catch {
      setCollaborators([]);
      return [];
    } finally {
      setIsCollaboratorsLoading(false);
    }
  }, [onGetCollaboratorsService, lodosUserId]);

  const fetchInvitations = useCallback(async (): Promise<MeMeetInvitation[]> => {
    if (!onGetInvitationsService || !roomId) {
      setInvitations([]);
      return [];
    }

    setIsInvitationsLoading(true);
    try {
      const response = await onGetInvitationsService({ roomID: roomId });
      const next = normalizeInvitationsResponse(response);
      setInvitations(next);
      return next;
    } catch {
      setInvitations([]);
      return [];
    } finally {
      setIsInvitationsLoading(false);
    }
  }, [onGetInvitationsService, roomId]);

  const checkInvitedForTarget = useCallback(
    async ({
      key,
      userID,
      email,
    }: {
      key: string;
      userID?: string;
      email?: string;
    }) => {
      const normalizedUserID = String(userID || "").trim();
      const normalizedEmail = String(email || "").trim();
      if (!onIsInvitedUserService || !roomId || (!normalizedUserID && !normalizedEmail)) return;

      setIsCheckingInvitedByKey((prev) => ({ ...prev, [key]: true }));
      try {
        const response = await onIsInvitedUserService({
          roomID: roomId,
          userID: normalizedUserID || undefined,
          email: normalizedEmail || undefined,
        });
        const isInvited = readResponseStatus(response);
        setInvitedStatusByKey((prev) => ({ ...prev, [key]: isInvited }));
      } catch {
        setInvitedStatusByKey((prev) => ({ ...prev, [key]: false }));
      } finally {
        setIsCheckingInvitedByKey((prev) => ({ ...prev, [key]: false }));
      }
    },
    [onIsInvitedUserService, roomId]
  );

  const refreshInvitedChecks = useCallback(
    async (nextCollaborators: CollaboratorOption[], nextInvitations: MeMeetInvitation[]) => {
      if (!onIsInvitedUserService || !roomId) {
        setInvitedStatusByKey({});
        setIsCheckingInvitedByKey({});
        return;
      }

      const checks: Promise<void>[] = [];

      nextCollaborators.forEach((item) => {
        const maybeSyntheticId = String(item.id || "");
        const userID = maybeSyntheticId.startsWith("email:") ? "" : maybeSyntheticId;
        const key = makeInvitedCheckKey("collab", userID, item.email);
        checks.push(checkInvitedForTarget({ key, userID, email: item.email }));
      });

      nextInvitations.forEach((item) => {
        const key = makeInvitedCheckKey("invite", item.userID, item.email);
        checks.push(checkInvitedForTarget({ key, userID: item.userID, email: item.email }));
      });

      if (checks.length > 0) {
        await Promise.all(checks.map((task) => task.catch(() => undefined)));
      }
    },
    [onIsInvitedUserService, roomId, checkInvitedForTarget]
  );

  const closeInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
    setInviteActionError("");
    setInviteActionSuccess("");
    setSelectedCollaboratorId("");
    setCollaboratorSearchQuery("");
    setInviteEmail("");
    setInviteNameLastname("");
    setInvitationToRemove(null);
  }, []);

  const openInviteModal = useCallback(() => {
    setInviteActionError("");
    setInviteActionSuccess("");
    setCollaboratorSearchQuery("");
    setIsInviteModalOpen(true);
  }, []);

  const handleInviteSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!onInviteUserService || !roomId) return;

      const normalizedSelectedCollaboratorId = String(selectedCollaboratorId || "").trim();
      const normalizedEmail = String(inviteEmail || "").trim();
      const normalizedNameLastname = String(inviteNameLastname || "").trim();

      let payload:
        | { roomID: string; userID: string; email?: undefined; nameLastname?: undefined }
        | { roomID: string; email: string; nameLastname: string; userID?: undefined }
        | null = null;

      if (normalizedSelectedCollaboratorId) {
        if (normalizedSelectedCollaboratorId.startsWith("email:")) {
          setInviteActionError(t("memeetInvalidCollaboratorSelection", "Selected collaborator is invalid."));
          return;
        }
        payload = { roomID: roomId, userID: normalizedSelectedCollaboratorId };
      } else if (normalizedEmail) {
        if (!normalizedNameLastname) {
          setInviteActionError(
            t("memeetNameLastnameRequiredForEmailInvite", "Name and lastname are required for email invitation.")
          );
          return;
        }
        payload = { roomID: roomId, email: normalizedEmail, nameLastname: normalizedNameLastname };
      }

      if (!payload) {
        setInviteActionError(
          t("memeetInviteSelectionRequired", "Select a collaborator or fill the email invitation form.")
        );
        return;
      }

      setIsInviteSubmitting(true);
      setInviteActionError("");
      setInviteActionSuccess("");

      try {
        const selectedNotify = Object.keys(notifyChannels).filter(
          (k) => (notifyChannels as Record<string, boolean>)[k]
        );
        const response = await onInviteUserService({ ...payload, notify: selectedNotify });
        // Katilim baglantisi: backend doner (memeet.php JOIN_BASE). Kullanici
        // hicbir kanal secmediyse tek yolu budur.
        const link =
          (response as any)?.result?.joinLink ??
          (response as any)?.data?.result?.joinLink ??
          (response as any)?.joinLink ??
          "";
        if (typeof link === "string" && link) setInviteJoinLink(link);
        if (!readResponseStatus(response)) {
          setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
          return;
        }

        const latestInvitations = await fetchInvitations();
        await refreshInvitedChecks(collaborators, latestInvitations);

        setSelectedCollaboratorId("");
        setInviteEmail("");
        setInviteNameLastname("");
        setInviteActionSuccess(t("memeetInvitationSent", "Invitation sent."));
        // Modal ACIK kalir: kullanici katilim baglantisini gorup kopyalayabilsin.
        // (Eskiden hemen kapaniyordu; link gosterilecegi icin artik kapanmiyor.)
      } catch {
        setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
      } finally {
        setIsInviteSubmitting(false);
      }
    },
    [
      onInviteUserService,
      notifyChannels,
      roomId,
      selectedCollaboratorId,
      inviteEmail,
      inviteNameLastname,
      t,
      fetchInvitations,
      refreshInvitedChecks,
      collaborators,
    ]
  );

  const handleConfirmRemoveInvitation = useCallback(async () => {
    const code = String(invitationToRemove?.invitationCode || "").trim();
    if (!onRemoveInvitationService || !code) return;

    setIsRemovingInvitation(true);
    setInviteActionError("");
    try {
      const response = await onRemoveInvitationService({ invitationCode: code });
      if (!readResponseStatus(response)) {
        setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
        return;
      }
      setInvitationToRemove(null);
      const latestInvitations = await fetchInvitations();
      await refreshInvitedChecks(collaborators, latestInvitations);
    } catch {
      setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
    } finally {
      setIsRemovingInvitation(false);
    }
  }, [
    invitationToRemove,
    onRemoveInvitationService,
    t,
    fetchInvitations,
    refreshInvitedChecks,
    collaborators,
  ]);

  const handleParticipantActionClick = useCallback(
    async (participant: Participant) => {
      if (!onSendDmOnSocialHive || participant.isLocal) return;
      const targetUserId = participant.userId;
      if (!targetUserId) return;
      await onSendDmOnSocialHive(targetUserId, participant.name);
    },
    [onSendDmOnSocialHive]
  );

  const handleLanguageChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      changeLanguage?.(e.target.value);
    },
    [changeLanguage]
  );

  // Keep localStreamRef in sync
  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  useEffect(() => {
    localMediaStateRef.current.audioEnabled = isAudioEnabled;
    localMediaStateRef.current.videoEnabled = isVideoEnabled;
  }, [isAudioEnabled, isVideoEnabled]);

  useEffect(() => {
    localHandRaisedRef.current = isHandRaised;
  }, [isHandRaised]);

  useEffect(() => {
    remoteParticipantsRef.current = remoteParticipants;
  }, [remoteParticipants]);

  useEffect(() => {
    chatOpenRef.current = isChatOpen;
    if (isChatOpen) setUnreadChatCount(0);
  }, [isChatOpen]);

  useEffect(() => {
    if (!resolvedWorkspaces.length) return;
    if (selectedChecklistWorkspaceId && workspaceIdSet.has(String(selectedChecklistWorkspaceId))) return;
    setSelectedChecklistWorkspaceId(resolvedWorkspaces[0]?.id || "");
  }, [resolvedWorkspaces, selectedChecklistWorkspaceId, workspaceIdSet]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      if (!hasChecklistWorkspaceSelection) return;
      if (!selectedChecklistWorkspaceId) return;
      window.localStorage.setItem("memeet:lastChecklistWorkspaceId", String(selectedChecklistWorkspaceId));
    } catch {
      // ignore
    }
  }, [selectedChecklistWorkspaceId, hasChecklistWorkspaceSelection]);

  useEffect(() => {
    if (!roomId || !canLoadInvitations) return;
    void fetchInvitations();
  }, [roomId, canLoadInvitations, fetchInvitations]);

  useEffect(() => {
    if (!isInviteModalOpen) return;

    let cancelled = false;
    (async () => {
      const [nextCollaborators, nextInvitations] = await Promise.all([
        fetchCollaborators(),
        fetchInvitations(),
      ]);
      if (cancelled) return;
      await refreshInvitedChecks(nextCollaborators, nextInvitations);
    })();

    return () => {
      cancelled = true;
    };
  }, [isInviteModalOpen, fetchCollaborators, fetchInvitations, refreshInvitedChecks]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Escape") return;
        setChatContextMenu(null);
        if (!addingChecklistForMessageId) {
          setIsChecklistPickerOpen(false);
          setChecklistPickerMessageId(null);
          setChecklistActionError("");
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    } catch {
      return;
    }
  }, [addingChecklistForMessageId]);

  useEffect(() => {
    if (!chatContextMenu) return;

    const onClick = () => setChatContextMenu(null);
    const onScrollOrResize = () => setChatContextMenu(null);

    window.addEventListener("click", onClick);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [chatContextMenu]);

  const {
    status: socketStatus,
    roomId: socketRoomId,
    callId,
    userId: localUserId,
    iceServers,
    connect,
    setNickname,
    joinRoom,
    leaveRoom,
    startCall: socketStartCall,
    leaveCall,
    sendSignal,
    toggleMedia,
  } = useSocket({
    token,
    autoConnect: Boolean(token),
    handlers: {
      onCallStarted: ({ callId: newCallId }) => {
        console.log("Call started:", newCallId);
        debugLog("call-started-event", { callId: newCallId, previousCallStatus: callStatus });
        if (callStatus === "idle") return;
        setCallStatus("in-call");
      },
      onUserJoined: async ({ user, participantCount }) => {
        console.log("User joined:", user.nickname, "Total:", participantCount, "odaHakları:", user.odaHakları);
        debugLog("user-joined-event", {
          userId: user.odaHakları,
          participantCount,
          localUserId,
          callStatus,
        });

        if (!user.odaHakları) {
          console.error("User joined without odaHakları!", user);
          return;
        }

        if (localUserId && user.odaHakları === localUserId) {
          return;
        }

        const identity = parseIdentityNickname(user.nickname);
        const participant: RemoteParticipant = {
          id: user.odaHakları,
          displayName: identity.displayName,
          avatarUrl: identity.avatarUrl,
          lodosUserId: identity.lodosUserId,
          isAudioEnabled: true,
          isVideoEnabled: true,
          isScreenSharing: false,
          screenShareMid: null,
          isHandRaised: false,
        };

        setRemoteParticipants((prev) => new Map(prev).set(participant.id, participant));
        if (callStatus === "idle") return;
        if (callStatus === "in-call") {
          playNotificationTone("join");
        }
        requestRemoteMediaState(participant.id);
        sendLocalMediaState(participant.id);
        requestRemoteScreenShareState(participant.id);
        sendLocalScreenShareState(participant.id);
        requestRemoteHandState(participant.id);
        sendLocalHandState(participant.id);

        // Wait until both local media and local socket id are ready before deciding offer ownership.
        if (!localStreamRef.current || !localUserIdRef.current) {
          console.log("Peer prerequisites missing, queuing user:", user.odaHakları);
          queuePendingUser(participant);
          return;
        }

        // Create peer connection for new user
        await createPeerConnection(user.odaHakları, shouldCreateOffer(localUserIdRef.current, user.odaHakları));
      },
      onUserLeft: ({ user, remainingCount }) => {
        console.log("User left:", user.nickname, "Remaining:", remainingCount);
        debugLog("user-left-event", { userId: user.odaHakları, remainingCount, localUserId, callStatus });
        if (!user.odaHakları) return;
        if (localUserId && user.odaHakları === localUserId) return;
        setRemoteParticipants((prev) => {
          const next = new Map(prev);
          next.delete(user.odaHakları);
          return next;
        });
        setRemoteStreams((prev) => {
          const next = new Map(prev);
          next.delete(user.odaHakları);
          return next;
        });
        closePeerConnection(user.odaHakları);
        pendingCandidatesRef.current.delete(user.odaHakları);
        pendingUsersRef.current.delete(user.odaHakları);
      },
      onCallEnded: ({ reason }) => {
        console.log("Call ended:", reason);
        debugLog("call-ended-event", { reason, callStatus });
        if (callStatus === "idle") return;
        handleEndCall();
      },
      onSignal: async ({ from, fromNickname, signalType, data }) => {
        console.log("Signal received:", signalType, "from:", from);
        debugLog("signal-received", {
          from,
          signalType,
          callStatus,
          fromNicknamePresent: Boolean(fromNickname),
          hasPayload: data !== null && data !== undefined,
        });
        if (callStatus === "idle") return;
        await handleSignal(from, fromNickname, signalType, data);
      },
      onMediaChanged: ({ user, mediaType, enabled }) => {
        debugLog("media-changed-event", {
          from: user.odaHakları,
          mediaType,
          enabled,
          localUserId,
        });
        if (!user.odaHakları) return;
        if (localUserId && user.odaHakları === localUserId) return;
        setRemoteParticipants((prev) => {
          const next = new Map(prev);
          const identity = parseIdentityNickname(user.nickname);
          const existing = next.get(user.odaHakları) || {
            id: user.odaHakları,
            displayName: identity.displayName,
            avatarUrl: identity.avatarUrl,
            lodosUserId: identity.lodosUserId,
            isAudioEnabled: true,
            isVideoEnabled: true,
            isScreenSharing: false,
            screenShareMid: null,
            isHandRaised: false,
          };

          if (!existing.displayName) existing.displayName = identity.displayName;
          if (!existing.avatarUrl && identity.avatarUrl) existing.avatarUrl = identity.avatarUrl;
          if (!existing.lodosUserId && identity.lodosUserId) existing.lodosUserId = identity.lodosUserId;

          if (mediaType === "audio") existing.isAudioEnabled = enabled;
          if (mediaType === "video") {
            existing.isVideoEnabled = enabled;
          }

          next.set(user.odaHakları, existing);
          return next;
        });
      },
      onReconnectedToCall: ({ callId: reconnectedCallId, participants }) => {
        console.log(
          "[VideoCall] Socket auto-rejoined call",
          reconnectedCallId,
          "participants:",
          participants.length
        );
        debugLog("reconnected-to-call", {
          callId: reconnectedCallId,
          participantCount: participants.length,
        });

        // Our old peer connections are stale — both sides had different
        // socket.ids before, and the server has broadcast call-user-left
        // for our old id to the other participants (via ghost eviction).
        // They will rebuild their PCs for us when they see call-user-joined.
        // We must mirror that on this side: close everything and rebuild
        // against the fresh roster the server just handed us.
        try {
          peerConnectionsRef.current.forEach((pc, peerId) => {
            try {
              pc.close();
            } catch {
              /* ignore */
            }
            clearPeerRuntimeMappings(peerId);
          });
          peerConnectionsRef.current.clear();
          pendingCandidatesRef.current.clear();
          chatChannelsRef.current.forEach((channel) => {
            try {
              channel.close();
            } catch {
              /* ignore */
            }
          });
          chatChannelsRef.current.clear();
          setRemoteStreams(new Map());
        } catch (err) {
          console.error("[VideoCall] Failed to tear down stale peer state on reconnect:", err);
        }

        // Rebuild peer connections for every participant the server reports.
        // We cannot rely on onUserJoined firing for existing participants
        // because the server only broadcasts that to *other* peers, not to
        // the rejoining user. We must proactively iterate the roster.
        (async () => {
          for (const participant of participants) {
            if (!participant?.odaHakları) continue;
            if (localUserIdRef.current && participant.odaHakları === localUserIdRef.current) continue;

            const identity = parseIdentityNickname(participant.nickname);
            const remote: RemoteParticipant = {
              id: participant.odaHakları,
              displayName: identity.displayName,
              avatarUrl: identity.avatarUrl,
              lodosUserId: identity.lodosUserId,
              isAudioEnabled: true,
              isVideoEnabled: true,
              isScreenSharing: false,
              screenShareMid: null,
              isHandRaised: false,
            };
            setRemoteParticipants((prev) => new Map(prev).set(remote.id, remote));

            requestRemoteMediaState(remote.id);
            sendLocalMediaState(remote.id);
            requestRemoteScreenShareState(remote.id);
            sendLocalScreenShareState(remote.id);
            requestRemoteHandState(remote.id);
            sendLocalHandState(remote.id);

            if (!localStreamRef.current || !localUserIdRef.current) {
              queuePendingUser(remote);
              continue;
            }

            try {
              await createPeerConnection(
                remote.id,
                shouldCreateOffer(localUserIdRef.current, remote.id)
              );
            } catch (err) {
              console.error("[VideoCall] Failed to rebuild peer connection on reconnect:", remote.id, err);
            }
          }
        })();
      },
    },
  });

  useEffect(() => {
    if (!isMeMeetDebugEnabled()) return;
    debugLog("socket-state", {
      socketStatus,
      socketRoomId,
      callId,
      localUserId,
      targetRoomId: roomId,
      callStatus,
    });
  }, [socketStatus, socketRoomId, callId, localUserId, roomId, callStatus]);

  useEffect(() => {
    return () => {
      const audioContext = notificationAudioContextRef.current;
      notificationAudioContextRef.current = null;
      if (!audioContext || audioContext.state === "closed") return;
      void audioContext.close().catch(() => undefined);
    };
  }, []);

  const applyJoinRoomRoster = useCallback(
    (users: Array<{ odaHakları: string; nickname: string }>) => {
      setRemoteParticipants(() => {
        const next = new Map<string, RemoteParticipant>();
        users.forEach((user) => {
          if (!user.odaHakları) return;
          if (localUserId && user.odaHakları === localUserId) return;
          const identity = parseIdentityNickname(user.nickname);
          next.set(user.odaHakları, {
            id: user.odaHakları,
            displayName: identity.displayName,
            avatarUrl: identity.avatarUrl,
            lodosUserId: identity.lodosUserId,
            isAudioEnabled: true,
            isVideoEnabled: true,
            isScreenSharing: false,
            screenShareMid: null,
            isHandRaised: false,
          });
        });
        return next;
      });
    },
    [localUserId]
  );

  useEffect(() => {
    if (!localUserId) return;
    setRemoteParticipants((prev) => {
      if (!prev.has(localUserId)) return prev;
      const next = new Map(prev);
      next.delete(localUserId);
      return next;
    });
  }, [localUserId]);

  useEffect(() => {
    localUserIdRef.current = localUserId;
  }, [localUserId]);

  const queuePendingUser = useCallback((participant: RemoteParticipant) => {
    pendingUsersRef.current.set(participant.id, participant);
    debugLog("pc-user-deferred", {
      peerId: participant.id,
      hasLocalStream: Boolean(localStreamRef.current),
      hasLocalUserId: Boolean(localUserIdRef.current),
    });
  }, []);

  const resolveVideoSenderSlotsForPeer = useCallback(
    (
      peerId: string,
      pc: RTCPeerConnection,
      localCameraTrack: MediaStreamTrack | null | undefined,
      options?: { ensureTwoSlots?: boolean }
    ): VideoSenderSlots => {
      if (options?.ensureTwoSlots) {
        ensurePeerConnectionVideoSenderCount(pc, 2);
      }

      const senders = getPeerConnectionVideoSenders(pc);
      const previous = videoSenderSlotsRef.current.get(peerId);
      const isValidSender = (sender: RTCRtpSender | null | undefined) => Boolean(sender && senders.includes(sender));

      const preferredSlots: VideoSenderSlots | null = previous
        ? {
            cameraSender: isValidSender(previous.cameraSender) ? previous.cameraSender : null,
            screenSender: isValidSender(previous.screenSender) ? previous.screenSender : null,
          }
        : null;

      const activeScreenTrack = screenStreamRef.current?.getVideoTracks?.()?.[0] ?? null;
      const resolved = resolveVideoSenderSlots(pc, localCameraTrack, preferredSlots, activeScreenTrack);
      videoSenderSlotsRef.current.set(peerId, resolved);
      return resolved;
    },
    []
  );

  const renegotiatePeerConnection = useCallback(
    async (peerId: string, pc: RTCPeerConnection, reason: string): Promise<boolean> => {
      if (pc.signalingState !== "stable") {
        debugLog("pc-renegotiate-skip", { peerId, reason, signalingState: pc.signalingState });
        return false;
      }

      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendSignal(peerId, "offer", offer);
        debugLog("pc-renegotiate-offer-sent", {
          peerId,
          reason,
          signalingState: pc.signalingState,
          sdpLength: offer?.sdp?.length ?? 0,
        });
        return true;
      } catch (error) {
        debugLog("pc-renegotiate-offer-error", {
          peerId,
          reason,
          message: error instanceof Error ? error.message : String(error),
        });
        return false;
      }
    },
    [sendSignal]
  );

  const getScreenShareMidForPeer = useCallback(
    (peerId: string): string | undefined => {
      const pc = peerConnectionsRef.current.get(peerId);
      if (!pc) return undefined;

      const localCameraTrack = localStreamRef.current?.getVideoTracks?.()?.[0] ?? null;
      const { screenSender } = resolveVideoSenderSlotsForPeer(peerId, pc, localCameraTrack, {
        ensureTwoSlots: true,
      });
      return getSenderMid(pc, screenSender) ?? undefined;
    },
    [resolveVideoSenderSlotsForPeer]
  );

  const sendLocalMediaState = useCallback(
    (to: string) => {
      const audioEnabled = localMediaStateRef.current.audioEnabled;
      const videoEnabled = localMediaStateRef.current.videoEnabled;
      debugLog("send-local-media-state", { to, audioEnabled, videoEnabled, isScreenSharing });
      sendSignal(to, "media-state", { audioEnabled, videoEnabled });
    },
    [isScreenSharing, sendSignal]
  );

  const requestRemoteMediaState = useCallback(
    (to: string) => {
      sendSignal(to, "media-state-request", {});
    },
    [sendSignal]
  );

  const sendLocalScreenShareState = useCallback(
    (to: string) => {
      const mid = isScreenSharing ? getScreenShareMidForPeer(to) : undefined;
      debugLog("send-local-screen-share-state", { to, enabled: isScreenSharing, mid: mid ?? null });
      sendSignal(to, "screen-share-state", { enabled: isScreenSharing, mid });
    },
    [isScreenSharing, sendSignal, getScreenShareMidForPeer]
  );

  const requestRemoteScreenShareState = useCallback(
    (to: string) => {
      sendSignal(to, "screen-share-state-request", {});
    },
    [sendSignal]
  );

  const broadcastScreenShareState = useCallback(
    (enabled: boolean) => {
      debugLog("broadcast-screen-share-state", {
        enabled,
        targetCount: remoteParticipantsRef.current.size,
      });
      remoteParticipantsRef.current.forEach((_, participantId) => {
        const mid = enabled ? getScreenShareMidForPeer(participantId) : undefined;
        sendSignal(participantId, "screen-share-state", { enabled, mid });
      });
    },
    [sendSignal, getScreenShareMidForPeer]
  );

  const sendLocalHandState = useCallback(
    (to: string) => {
      sendSignal(to, "hand-state", { raised: localHandRaisedRef.current });
    },
    [sendSignal]
  );

  const requestRemoteHandState = useCallback(
    (to: string) => {
      sendSignal(to, "hand-state-request", {});
    },
    [sendSignal]
  );

  const broadcastLocalMediaState = useCallback(() => {
    const audioEnabled = localMediaStateRef.current.audioEnabled;
    const videoEnabled = localMediaStateRef.current.videoEnabled;

    debugLog("broadcast-local-media-state", {
      audioEnabled,
      videoEnabled,
      isScreenSharing,
      targetCount: remoteParticipantsRef.current.size,
    });

    remoteParticipantsRef.current.forEach((_, participantId) => {
      sendSignal(participantId, "media-state", { audioEnabled, videoEnabled });
    });
  }, [isScreenSharing, sendSignal]);

  // Keep iceServersRef in sync
  useEffect(() => {
    iceServersRef.current = iceServers;
  }, [iceServers]);

  // Process pending users when stream becomes available
  useEffect(() => {
    if (!localStream || !localUserId) return;
    if (pendingUsersRef.current.size === 0) return;

    console.log("Processing pending users:", pendingUsersRef.current.size);
    const usersToProcess = Array.from(pendingUsersRef.current.entries());
    pendingUsersRef.current.clear();

    (async () => {
      for (const [id] of usersToProcess) {
        try {
          console.log("Creating peer connection for pending user:", id);
          await createPeerConnection(id, shouldCreateOffer(localUserId, id));
        } catch (err) {
          console.error("Failed to create peer connection for pending user:", id, err);
        }
      }
    })();
  }, [localStream, localUserId]);

  useEffect(() => {
    if (hasStartedPreviewRef.current) return;
    hasStartedPreviewRef.current = true;
    debugLog("preview-start-request", { roomId });
    startStream({ audio: true, video: true }).catch((error) => {
      console.error("Failed to start preview:", error);
      debugLog("preview-start-error", { message: error instanceof Error ? error.message : String(error) });
    });
  }, [startStream, roomId]);

  useEffect(() => {
    if (!isMeMeetDebugEnabled()) return;
    debugLog("local-stream-state", {
      isAudioEnabled,
      isVideoEnabled,
      tracks: describeMediaStreamTracks(localStream),
    });
  }, [localStream, isAudioEnabled, isVideoEnabled]);

  useEffect(() => {
    if (socketStatus !== "connected") return;
    if (callStatus !== "idle") return;
    if (socketRoomId === roomId) return;

    debugLog("join-room-request", {
      roomId,
      socketRoomId,
      encodedNicknameLength: encodedNickname.length,
      localUserId,
    });
    setNickname(encodedNickname);
    joinRoom(roomId, (data) => {
      const roster = parseJoinRoomRosterAck(data);
      debugLog("join-room-ack", {
        roomId,
        rosterCount: roster.length,
        rosterIds: roster.map((entry) => entry.odaHakları),
      });
      applyJoinRoomRoster(roster);
    });
  }, [socketStatus, callStatus, socketRoomId, roomId, setNickname, encodedNickname, joinRoom, applyJoinRoomRoster, localUserId]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const inCall = callStatus === "in-call";
      window.dispatchEvent(new CustomEvent("memeet:call-status", { detail: { inCall } }));
    } catch {
      // ignore
    }
  }, [callStatus]);

  useEffect(() => {
    return () => {
      try {
        if (typeof window === "undefined") return;
        window.dispatchEvent(new CustomEvent("memeet:call-status", { detail: { inCall: false } }));
      } catch {
        // ignore
      }
    };
  }, []);

  // Set preview video
  useEffect(() => {
    if (previewVideoRef.current && localStream) {
      previewVideoRef.current.srcObject = localStream;
    }
  }, [localStream, isVideoEnabled, callStatus]);

  const updateChatScrollLock = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return;
    const threshold = 48;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollChatRef.current = distanceToBottom < threshold;
  }, []);

  useEffect(() => {
    if (!isChatOpen) return;
    const el = chatListRef.current;
    if (!el) return;
    if (!shouldAutoScrollChatRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [chatMessages.length, isChatOpen]);

  useEffect(() => {
    if (!getLinkPreview) return;
    const authToken =
      token ||
      (typeof window !== "undefined" ? window.localStorage?.getItem("token") : "") ||
      "";
    if (!authToken) return;
    const allUrls = new Set<string>();
    chatMessages.forEach((m) => {
      extractUrls(m.text).forEach((u) => allUrls.add(u));
    });
    const toFetch = Array.from(allUrls).filter((u) => !linkPreviews[u] && !fetchingUrlsRef.current.has(u));
    if (toFetch.length === 0) return;

    let canceled = false;
    (async () => {
      for (const url of toFetch) {
        if (canceled) return;
        try {
          fetchingUrlsRef.current.add(url);
          const res = await getLinkPreview(authToken, url);
          const ok = Boolean(res?.data?.status);
          const result = res?.data?.result;
          if (!ok || !result) {
            if (!canceled) setLinkPreviews((prev) => ({ ...prev, [url]: { error: true } }));
            continue;
          }

          const nextUrl = normalizeUrl(result.url || url) || url;
          if (!canceled) {
            setLinkPreviews((prev) => ({
              ...prev,
              [url]: {
                title: String(result.title || ""),
                description: String(result.description || ""),
                image: result.image ? String(result.image) : null,
                url: nextUrl,
              },
            }));
          }
        } catch {
          if (!canceled) setLinkPreviews((prev) => ({ ...prev, [url]: { error: true } }));
        } finally {
          fetchingUrlsRef.current.delete(url);
        }
      }
    })();

    return () => {
      canceled = true;
    };
  }, [chatMessages, getLinkPreview, token, linkPreviews]);

  useEffect(() => {
    if (!isChatOpen) return;
    window.setTimeout(() => {
      chatDraftInputRef.current?.focus();
    }, 0);
  }, [isChatOpen]);

  const appendChatMessage = useCallback((message: ChatMessage, opts?: { bumpUnread?: boolean }) => {
    setChatMessages((prev) => {
      if (chatMessageIdsRef.current.has(message.id)) return prev;
      chatMessageIdsRef.current.add(message.id);
      const next = [...prev, message];

      if (next.length > MAX_CHAT_MESSAGES) {
        const overflow = next.length - MAX_CHAT_MESSAGES;
        const removed = next.splice(0, overflow);
        removed.forEach((m) => chatMessageIdsRef.current.delete(m.id));
      }
      return next;
    });

    if (opts?.bumpUnread && !chatOpenRef.current) {
      setUnreadChatCount((c) => Math.min(99, c + 1));
      playNotificationTone("chat");
    }
  }, [playNotificationTone]);

  const applyChecklistAddedEvent = useCallback((ev: ChatWireChecklistAddedV1) => {
    setChecklistAddsByMessageId((prev) => {
      const current = prev[ev.messageId] || [];
      const workspaceId = String(ev.workspaceId || "").trim();
      const checklistName = String(ev.checklistName || "").trim();
      if (!workspaceId || !checklistName) return prev;

      const already = current.some(
        (x) =>
          String(x.workspaceId) === workspaceId &&
          String(x.checklistId || "") === String(ev.checklistId || "") &&
          String(x.checklistName) === checklistName
      );
      if (already) return prev;

      const nextItem: ChatChecklistAddMeta = {
        workspaceId,
        checklistId: ev.checklistId ? String(ev.checklistId) : undefined,
        checklistName,
        byUserId: ev.byUserId ? String(ev.byUserId) : undefined,
        byName: ev.byName ? String(ev.byName) : undefined,
        addedAt: typeof ev.ts === "number" ? ev.ts : Date.now(),
        preview: ev.preview ? String(ev.preview) : undefined,
      };

      return { ...prev, [ev.messageId]: [...current, nextItem] };
    });
  }, []);

  const broadcastChecklistAddedEvent = useCallback((ev: ChatWireChecklistAddedV1) => {
    const payloadObj: ChatWireMessageV1 = {
      v: 1,
      type: "chat-message",
      id: makeChatId(),
      ts: typeof ev.ts === "number" ? ev.ts : Date.now(),
      text: " ",
      from: {
        name: String(effectiveDisplayName || "User"),
        avatarUrl: avatarUrl || undefined,
      },
      meta: {
        kind: "checklist-added",
        messageId: String(ev.messageId || ""),
        workspaceId: String(ev.workspaceId || ""),
        checklistName: String(ev.checklistName || ""),
        checklistId: ev.checklistId ? String(ev.checklistId) : undefined,
        byUserId: ev.byUserId ? String(ev.byUserId) : undefined,
        byName: ev.byName ? String(ev.byName) : undefined,
        preview: ev.preview ? String(ev.preview) : undefined,
      },
    };

    const payload = JSON.stringify(payloadObj);
    chatChannelsRef.current.forEach((channel) => {
      if (channel.readyState !== "open") return;
      try {
        channel.send(payload);
      } catch (error) {
        console.error("Failed to send checklist added chat event:", error);
      }
    });
  }, [effectiveDisplayName, avatarUrl]);

  const openChatContextMenu = useCallback((e: MouseEvent, messageId: string) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      if (typeof window === "undefined") return;
      const menuWidth = 220;
      const menuHeight = 96;
      const padding = 8;

      const x = Math.min(e.clientX, window.innerWidth - menuWidth - padding);
      const y = Math.min(e.clientY, window.innerHeight - menuHeight - padding);

      setChatContextMenu({
        messageId,
        x: Math.max(padding, x),
        y: Math.max(padding, y),
      });
    } catch {
      // ignore
    }
  }, []);

  const openChecklistPickerForMessage = useCallback((messageId: string) => {
    setChecklistActionError("");
    setChecklistPickerMessageId(messageId);
    setIsChecklistPickerOpen(true);
  }, []);

  const openAddToChecklistPicker = useCallback(() => {
    const messageId = chatContextMenu?.messageId;
    setChatContextMenu(null);
    if (!messageId) return;
    openChecklistPickerForMessage(messageId);
  }, [chatContextMenu, openChecklistPickerForMessage]);

  const closeAddToChecklistPicker = useCallback((opts?: { force?: boolean }) => {
    if (!opts?.force && addingChecklistForMessageId) return;
    setIsChecklistPickerOpen(false);
    setChecklistPickerMessageId(null);
    setChecklistActionError("");
  }, [addingChecklistForMessageId]);

  const handleAddToNotes = useCallback(
    async (messageId: string) => {
      if (!canAddToNotes || isAddingToNotesForMessageId) return;
      const msg = chatMessages.find((m) => m.id === messageId);
      if (!msg) return;

      setIsAddingToNotesForMessageId(messageId);
      try {
        const result = await onAddChatMessageToNotes?.({
          meetingId: String(roomId || ""),
          meetingStartedAt: meetingStartedAtRef.current || Date.now(),
          message: {
            id: msg.id,
            text: msg.text,
            ts: msg.ts,
            fromId: msg.fromId,
            fromName: msg.fromName,
          },
        });

        setNotesAddedByMessageId((prev) => ({
          ...prev,
          [messageId]: {
            addedAt: Date.now(),
            noteId: result?.noteId,
            noteTitle: result?.noteTitle,
          },
        }));
      } catch (error) {
        console.error("Failed to add chat message to notes:", error);
      } finally {
        setIsAddingToNotesForMessageId(null);
      }
    },
    [canAddToNotes, chatMessages, isAddingToNotesForMessageId, onAddChatMessageToNotes, roomId]
  );

  const addChatMessageToChecklist = useCallback(
    async (messageId: string, workspaceId: string, opts?: { closePicker?: boolean }) => {
      if (!canAddToChecklist) {
        setChecklistActionError(t("memeetChecklistIntegrationMissing", "Checklist integration is unavailable."));
        return;
      }

      const wsId = String(workspaceId || "").trim();
      if (!wsId) {
        setChecklistActionError(t("memeetSelectWorkspace", "Please select a workspace."));
        return;
      }

      const msg = chatMessages.find((m) => m.id === messageId);
      if (!msg) {
        setChecklistActionError(t("memeetMessageNotFound", "Message not found."));
        return;
      }

      setChecklistActionError("");
      setAddingChecklistForMessageId(messageId);
      const now = Date.now();

      try {
        const result = await onAddChatMessageToChecklist?.({
          workspaceId: wsId,
          meetingId: String(roomId || ""),
          meetingStartedAt: meetingStartedAtRef.current || now,
          message: {
            id: msg.id,
            text: msg.text,
            ts: msg.ts,
            fromId: msg.fromId,
            fromName: msg.fromName,
          },
        });

        const checklistName = String(result?.checklistName || roomId || "").trim() || t("memeetChecklist", "Checklist");
        const checklistId = result?.checklistId ? String(result.checklistId) : undefined;
        const preview = truncateChatPreview(msg.text);

        applyChecklistAddedEvent({
          v: 1,
          type: "chat-checklist-added",
          ts: now,
          messageId: msg.id,
          workspaceId: wsId,
          checklistName,
          checklistId,
          byUserId: lodosUserId ? String(lodosUserId) : undefined,
          byName: effectiveDisplayName ? String(effectiveDisplayName) : undefined,
          preview,
        });

        broadcastChecklistAddedEvent({
          v: 1,
          type: "chat-checklist-added",
          ts: now,
          messageId: msg.id,
          workspaceId: wsId,
          checklistName,
          checklistId,
          byUserId: lodosUserId ? String(lodosUserId) : undefined,
          byName: effectiveDisplayName ? String(effectiveDisplayName) : undefined,
          preview,
        });

        if (opts?.closePicker) {
          setHasChecklistWorkspaceSelection(true);
          try {
            if (typeof window !== "undefined") {
              window.localStorage.setItem("memeet:hasChecklistWorkspaceSelection", "1");
              window.localStorage.setItem("memeet:lastChecklistWorkspaceId", String(wsId));
            }
          } catch {
            // ignore
          }
          closeAddToChecklistPicker({ force: true });
        }
      } catch (error) {
        console.error("Failed to add chat message to checklist:", error);
        setChecklistActionError(t("memeetChecklistAddFailed", "Couldn't add to checklist."));
      } finally {
        setAddingChecklistForMessageId(null);
      }
    },
    [
      canAddToChecklist,
      chatMessages,
      onAddChatMessageToChecklist,
      roomId,
      applyChecklistAddedEvent,
      broadcastChecklistAddedEvent,
      t,
      lodosUserId,
      effectiveDisplayName,
      closeAddToChecklistPicker,
    ]
  );

  const handleConfirmAddToChecklist = useCallback(async () => {
    if (!checklistPickerMessageId) return;
    const workspaceId = String(selectedChecklistWorkspaceId || "").trim();
    await addChatMessageToChecklist(checklistPickerMessageId, workspaceId, { closePicker: true });
  }, [addChatMessageToChecklist, checklistPickerMessageId, selectedChecklistWorkspaceId]);

  const handleQuickAddToChecklist = useCallback(
    async (messageId: string) => {
      if (!canAddToChecklist) return;
      if (addingChecklistForMessageId) return;

      const wsId = String(selectedChecklistWorkspaceId || "").trim();
      if (!hasChecklistWorkspaceSelection || !wsId || !workspaceIdSet.has(wsId)) {
        openChecklistPickerForMessage(messageId);
        return;
      }
      await addChatMessageToChecklist(messageId, wsId, { closePicker: false });
    },
    [
      canAddToChecklist,
      addingChecklistForMessageId,
      selectedChecklistWorkspaceId,
      hasChecklistWorkspaceSelection,
      workspaceIdSet,
      openChecklistPickerForMessage,
      addChatMessageToChecklist,
    ]
  );

  const registerChatChannel = useCallback(
    (peerId: string, channel: RTCDataChannel) => {
      chatChannelsRef.current.set(peerId, channel);

      channel.onmessage = (event) => {
        const raw = decodeRtcData(event.data);
        if (!raw) return;

        const soundboardMsg = parseSoundboardWireMessageV1(raw);
        if (soundboardMsg) {
          playSoundboardSound(soundboardMsg.soundId);
          return;
        }

        const checklistAdded = parseChatWireChecklistAddedV1(raw);
        if (checklistAdded) {
          applyChecklistAddedEvent(checklistAdded);
          return;
        }

        const parsed = parseChatWireMessageV1(raw);
        const text = String((parsed?.text ?? raw) || "").trim();
        const attachment = parsed?.attachment;
        if (!text && !attachment) return;

        const participant = remoteParticipantsRef.current.get(peerId);
        const fromName = String((parsed?.fromName ?? participant?.displayName ?? "User") || "User").trim() || "User";
        const fromAvatarUrl = parsed?.fromAvatarUrl ?? participant?.avatarUrl;

        appendChatMessage(
          {
            id: parsed?.id ?? makeChatId(),
            text,
            ts: parsed?.ts ?? Date.now(),
            fromId: peerId,
            fromName,
            fromAvatarUrl,
            isLocal: false,
            attachment,
          },
          { bumpUnread: true }
        );
      };

      channel.onclose = () => {
        const current = chatChannelsRef.current.get(peerId);
        if (current === channel) chatChannelsRef.current.delete(peerId);
      };
    },
    [appendChatMessage, applyChecklistAddedEvent, playSoundboardSound]
  );

  const rememberRemoteTrackMid = useCallback((peerId: string, mid: string, trackId: string) => {
    const current = remoteTrackMidToIdRef.current.get(peerId) || new Map<string, string>();
    current.set(mid, trackId);
    remoteTrackMidToIdRef.current.set(peerId, current);
  }, []);

  const removeRemoteTrackMappings = useCallback((peerId: string, trackId: string) => {
    const current = remoteTrackMidToIdRef.current.get(peerId);
    if (!current) return;

    let changed = false;
    current.forEach((mappedTrackId, mid) => {
      if (mappedTrackId !== trackId) return;
      current.delete(mid);
      changed = true;
    });

    if (!changed) return;
    if (current.size === 0) {
      remoteTrackMidToIdRef.current.delete(peerId);
      return;
    }
    remoteTrackMidToIdRef.current.set(peerId, current);
  }, []);

  const clearPeerRuntimeMappings = useCallback((peerId: string) => {
    videoSenderSlotsRef.current.delete(peerId);
    remoteTrackMidToIdRef.current.delete(peerId);
  }, []);

  const getRemoteTrackIdByMid = useCallback((peerId: string, mid: string): string | null => {
    const normalizedMid = String(mid || "").trim();
    if (!normalizedMid) return null;
    return remoteTrackMidToIdRef.current.get(peerId)?.get(normalizedMid) ?? null;
  }, []);

  function createPeerConnection(odaHakları: string, createOffer: boolean): Promise<RTCPeerConnection> {
    return new Promise(async (resolve) => {
      console.log("Creating peer connection for:", odaHakları, "createOffer:", createOffer);
      debugLog("pc-create-request", {
        peerId: odaHakları,
        createOffer,
        localUserId,
        hasLocalStream: Boolean(localStreamRef.current),
        localTracks: describeMediaStreamTracks(localStreamRef.current),
      });

      if (!odaHakları) {
        console.error("Cannot create peer connection: odaHakları is undefined");
        throw new Error("odaHakları is required");
      }

      if (peerConnectionsRef.current.has(odaHakları)) {
        console.log("Peer connection already exists for:", odaHakları);
        debugLog("pc-reuse", { peerId: odaHakları });
        resolve(peerConnectionsRef.current.get(odaHakları)!);
        return;
      }

      // Merge server-provided ICE servers with local STUN/TURN fallback so we
      // always have working candidates even if the server returns an empty or
      // partial list.
      const mergedIceServers = mergeIceServers(iceServersRef.current);
      const rtcConfig: RTCConfiguration = {
        iceServers: mergedIceServers,
        iceCandidatePoolSize: 10,
      };

      console.log("Using RTC config with", rtcConfig.iceServers?.length, "ICE servers (merged server+fallback)");
      const pc = new RTCPeerConnection(rtcConfig);
      peerConnectionsRef.current.set(odaHakları, pc);

      // Add local tracks to the connection
      const stream = localStreamRef.current;
      const hasLocalVideoTrack = Boolean(stream?.getVideoTracks().length);
      if (stream) {
        console.log("Adding", stream.getTracks().length, "local tracks to peer connection");
        stream.getTracks().forEach((track) => {
          pc.addTrack(track, stream);
        });
      } else {
        console.warn("No local stream available when creating peer connection");
      }

      if (!hasLocalVideoTrack) {
        ensurePeerConnectionVideoSenderCount(pc, 1);
      }

      // If we are currently screen sharing, add the active screen track to the
      // new peer connection BEFORE the initial offer/answer is generated, so
      // that the SDP carries a proper m= section for the screen with its own
      // stream id (msid). We use addTrack(track, screenStream) here rather
      // than addTransceiver() + replaceTrack() — the latter produces a track
      // with no associated stream on the receiver side (event.streams is
      // empty), which prevents gridParticipants from splitting the remote
      // stream into camera/screen tiles for a late joiner.
      const activeScreenTrack = screenStreamRef.current?.getVideoTracks()?.[0] ?? null;
      const activeScreenStream = screenStreamRef.current;
      let screenTrackAddedViaAddTrack = false;
      if (activeScreenTrack && activeScreenStream) {
        try {
          pc.addTrack(activeScreenTrack, activeScreenStream);
          screenTrackAddedViaAddTrack = true;
          debugLog("pc-screen-track-added-at-init", {
            peerId: odaHakları,
            trackId: activeScreenTrack.id,
            streamId: activeScreenStream.id,
          });
        } catch (error) {
          console.warn("Failed to addTrack active screen share at PC init:", error);
          debugLog("pc-screen-track-add-error", {
            peerId: odaHakları,
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }

      const localCameraTrack = stream?.getVideoTracks?.()?.[0] ?? null;
      const senderSlots = resolveVideoSenderSlotsForPeer(odaHakları, pc, localCameraTrack, {
        ensureTwoSlots: true,
      });
      debugLog("pc-sender-summary", {
        peerId: odaHakları,
        senderSummary: describePeerVideoSenders(pc, localCameraTrack),
        resolvedCameraTrackId: senderSlots.cameraSender?.track?.id ?? null,
        resolvedScreenTrackId: senderSlots.screenSender?.track?.id ?? null,
      });

      // Fallback path: if addTrack above failed (or no active screen), but
      // we do have an active screen track and a resolved empty slot, attach
      // it via replaceTrack as before. This keeps the original behaviour as
      // a safety net.
      if (activeScreenTrack && !screenTrackAddedViaAddTrack) {
        const screenSender = senderSlots.screenSender;
        if (screenSender) {
          try {
            await screenSender.replaceTrack(activeScreenTrack);
            debugLog("pc-screen-track-attached", {
              peerId: odaHakları,
              trackId: activeScreenTrack.id,
              senderSummary: describePeerVideoSenders(pc, localCameraTrack),
            });
          } catch (error) {
            console.warn("Failed to attach active screen share track to peer connection:", error);
            debugLog("pc-screen-track-attach-error", {
              peerId: odaHakları,
              message: error instanceof Error ? error.message : String(error),
            });
          }
        } else {
          debugLog("pc-screen-track-missing-sender", { peerId: odaHakları, trackId: activeScreenTrack.id });
        }
      }

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log("Sending ICE candidate to:", odaHakları);
          sendSignal(odaHakları, "ice-candidate", event.candidate.toJSON());
        }
      };

      pc.ontrack = (event) => {
        console.log("Remote track received from:", odaHakları);
        const [remoteStream] = event.streams;
        const mid = event.transceiver?.mid ?? null;
        if (mid) {
          rememberRemoteTrackMid(odaHakları, mid, event.track.id);
        }
        event.track.onended = () => {
          removeRemoteTrackMappings(odaHakları, event.track.id);
        };
        debugLog("pc-ontrack", {
          peerId: odaHakları,
          trackKind: event.track.kind,
          trackId: event.track.id,
          mid,
          streamCount: event.streams.length,
          streamTracks: describeMediaStreamTracks(remoteStream || null),
        });
        setRemoteStreams((prev) => {
          const next = new Map(prev);
          const existing = next.get(odaHakları);

          if (remoteStream) {
            if (existing && existing !== remoteStream) {
              existing.getTracks().forEach((track) => {
                if (!remoteStream.getTrackById(track.id)) {
                  remoteStream.addTrack(track);
                }
              });
            }

            next.set(odaHakları, remoteStream);
            return next;
          }

          const fallback = existing || new MediaStream();
          if (!fallback.getTrackById(event.track.id)) {
            fallback.addTrack(event.track);
          }
          next.set(odaHakları, fallback);
          return next;
        });
      };

      // Per-peer connect timeout — if the connection does not reach 'connected'
      // or 'completed' within this window, attempt an ICE restart. This guards
      // against cases where signaling succeeds but no media ever flows (e.g.
      // because one side was talking to a ghost socket id).
      const PEER_CONNECT_TIMEOUT_MS = 15000;
      let peerConnectTimer: number | null = window.setTimeout(() => {
        const state = pc.connectionState;
        const iceState = pc.iceConnectionState;
        if (state === "connected" || iceState === "connected" || iceState === "completed") {
          return;
        }

        // Safety: if the PC was already closed externally (e.g. by call-user-left
        // or a newer rebuild path), do NOT recreate — that would spawn a zombie
        // PC that keeps firing this timeout forever.
        if (state === "closed" || iceState === "closed") {
          console.log(
            `[VideoCall] Timeout fired for ${odaHakları} but PC is already closed. Skipping rebuild.`
          );
          if (peerConnectionsRef.current.get(odaHakları) === pc) {
            peerConnectionsRef.current.delete(odaHakları);
          }
          pendingCandidatesRef.current.delete(odaHakları);
          return;
        }

        // Safety: if the peer has left the call entirely, don't recreate.
        if (!remoteParticipantsRef.current.has(odaHakları)) {
          console.log(
            `[VideoCall] Timeout fired for ${odaHakları} but peer is no longer in the call. Cleaning up and skipping rebuild.`
          );
          try { pc.close(); } catch { /* ignore */ }
          clearPeerRuntimeMappings(odaHakları);
          if (peerConnectionsRef.current.get(odaHakları) === pc) {
            peerConnectionsRef.current.delete(odaHakları);
          }
          pendingCandidatesRef.current.delete(odaHakları);
          const staleChatForLeft = chatChannelsRef.current.get(odaHakları);
          if (staleChatForLeft) {
            try { staleChatForLeft.close(); } catch { /* ignore */ }
            chatChannelsRef.current.delete(odaHakları);
          }
          return;
        }

        console.warn(
          `[VideoCall] Peer ${odaHakları} did not connect within ${PEER_CONNECT_TIMEOUT_MS}ms (state=${state}, iceState=${iceState}). Tearing down and recreating PC from scratch.`
        );

        // Brute-force rebuild: an ICE restart on an already-stuck PC often
        // does nothing because no candidates ever reached either side. We
        // close the old PC entirely and, if we are the offerer, recreate
        // it. The answerer side waits for the fresh offer to arrive via
        // its own timeout + recreate cycle (both sides run this code).
        try {
          pc.close();
        } catch { /* ignore */ }
        clearPeerRuntimeMappings(odaHakları);
        if (peerConnectionsRef.current.get(odaHakları) === pc) {
          peerConnectionsRef.current.delete(odaHakları);
        }
        pendingCandidatesRef.current.delete(odaHakları);
        const staleChat = chatChannelsRef.current.get(odaHakları);
        if (staleChat) {
          try { staleChat.close(); } catch { /* ignore */ }
          chatChannelsRef.current.delete(odaHakları);
        }

        if (shouldCreateOffer(localUserIdRef.current, odaHakları)) {
          (async () => {
            try {
              await createPeerConnection(odaHakları, true);
            } catch (err) {
              console.error("[VideoCall] Failed to recreate peer connection after timeout:", err);
            }
          })();
        } else {
          // As the answerer we cannot generate a new offer ourselves. Create
          // a fresh (empty) PC and wait; the offerer's own timeout will fire
          // on their side and they will recreate + send a new offer, which
          // handleSignal will route into this PC via createPeerConnection.
          (async () => {
            try {
              await createPeerConnection(odaHakları, false);
            } catch (err) {
              console.error("[VideoCall] Failed to recreate answerer peer connection after timeout:", err);
            }
          })();
        }
      }, PEER_CONNECT_TIMEOUT_MS);

      const clearPeerConnectTimer = () => {
        if (peerConnectTimer !== null) {
          window.clearTimeout(peerConnectTimer);
          peerConnectTimer = null;
        }
      };

      pc.onconnectionstatechange = () => {
        console.log(`Connection state for ${odaHakları}:`, pc.connectionState);
        if (pc.connectionState === "connected") {
          clearPeerConnectTimer();
        }
        if (pc.connectionState === "closed" || pc.connectionState === "disconnected") {
          clearPeerConnectTimer();
        }
        if (pc.connectionState === "failed") {
          if (shouldCreateOffer(localUserIdRef.current, odaHakları)) {
            (async () => {
              try {
                const offer = await pc.createOffer({ iceRestart: true });
                await pc.setLocalDescription(offer);
                sendSignal(odaHakları, "offer", offer);
              } catch { /* ignore */ }
            })();
          }
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
          clearPeerConnectTimer();
        }
        if (pc.iceConnectionState === "failed") {
          pc.restartIce();
        }
      };

      pc.onicegatheringstatechange = () => {
        console.log(`ICE gathering state for ${odaHakları}:`, pc.iceGatheringState);
      };

      let negotiating = false;
      pc.onnegotiationneeded = async () => {
        if (negotiating) return;
        if (!shouldCreateOffer(localUserIdRef.current, odaHakları)) return;
        try {
          negotiating = true;
          const offer = await pc.createOffer();
          if (pc.signalingState !== "stable") return;
          await pc.setLocalDescription(offer);
          sendSignal(odaHakları, "offer", offer);
        } catch (err) {
          console.error("onnegotiationneeded error:", err);
        } finally {
          negotiating = false;
        }
      };

      pc.ondatachannel = (event) => {
        if (event.channel.label !== CHAT_CHANNEL_LABEL) return;
        registerChatChannel(odaHakları, event.channel);
      };

      if (createOffer) {
        if (!chatChannelsRef.current.has(odaHakları)) {
          registerChatChannel(odaHakları, pc.createDataChannel(CHAT_CHANNEL_LABEL));
        }

        try {
          console.log("Creating offer for:", odaHakları);
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          console.log("Sending offer to:", odaHakları);
          sendSignal(odaHakları, "offer", offer);
        } catch (err) {
          console.error("Failed to create/send offer for:", odaHakları, err);
        }
      }

      resolve(pc);
    });
  }

  async function processPendingCandidates(odaHakları: string, pc: RTCPeerConnection) {
    const pending = pendingCandidatesRef.current.get(odaHakları);
    if (pending && pending.length > 0) {
      console.log(`Processing ${pending.length} pending ICE candidates for ${odaHakları}`);
      for (const candidate of pending) {
        try {
          await pc.addIceCandidate(candidate);
        } catch (err) {
          console.error("Failed to add pending ICE candidate:", err);
        }
      }
      pendingCandidatesRef.current.delete(odaHakları);
    }
  }

  async function handleSignal(from: string, fromNickname: string | undefined, signalType: string, data: unknown) {
    if (localUserId && from === localUserId) return;
    if (signalType === "hand-state") {
      const payload = parseHandStateSignal(data);
      if (!payload) return;

      setRemoteParticipants((prev) => {
        const next = new Map(prev);
        const existing = next.get(from);
        const identity = parseIdentityNickname(fromNickname || "");
        const participant: RemoteParticipant = existing
          ? { ...existing }
          : {
              id: from,
              displayName: identity.displayName,
              avatarUrl: identity.avatarUrl,
              lodosUserId: identity.lodosUserId,
              isAudioEnabled: true,
              isVideoEnabled: true,
              isScreenSharing: false,
              screenShareMid: null,
              isHandRaised: false,
            };

        if (!participant.displayName) participant.displayName = identity.displayName;
        if (!participant.avatarUrl && identity.avatarUrl) participant.avatarUrl = identity.avatarUrl;
        if (!participant.lodosUserId && identity.lodosUserId) participant.lodosUserId = identity.lodosUserId;

        // Yalnız false→true geçişinde çın (moderatör + tüm katılımcılar duyar).
        if (payload.raised && !existing?.isHandRaised) playHandRaiseChime();
        participant.isHandRaised = payload.raised;

        next.set(from, participant);
        return next;
      });
      return;
    }

    if (signalType === "hand-state-request") {
      sendLocalHandState(from);
      return;
    }

    if (signalType === "media-state") {
      const payload = parseMediaStateSignal(data);
      if (!payload) return;

      setRemoteParticipants((prev) => {
        const next = new Map(prev);
        const existing = next.get(from);
        const identity = parseIdentityNickname(fromNickname || "");
        const participant: RemoteParticipant = existing
          ? { ...existing }
          : {
              id: from,
              displayName: identity.displayName,
              avatarUrl: identity.avatarUrl,
              lodosUserId: identity.lodosUserId,
              isAudioEnabled: true,
              isVideoEnabled: true,
              isScreenSharing: false,
              screenShareMid: null,
              isHandRaised: false,
            };

        if (!participant.displayName) participant.displayName = identity.displayName;
        if (!participant.avatarUrl && identity.avatarUrl) participant.avatarUrl = identity.avatarUrl;
        if (!participant.lodosUserId && identity.lodosUserId) participant.lodosUserId = identity.lodosUserId;

        participant.isAudioEnabled = payload.audioEnabled;
        participant.isVideoEnabled = payload.videoEnabled;

        next.set(from, participant);
        return next;
      });
      return;
    }

    if (signalType === "media-state-request") {
      sendLocalMediaState(from);
      return;
    }

    if (signalType === "screen-share-state") {
      const payload = parseScreenShareStateSignal(data);
      if (!payload) return;

      setRemoteParticipants((prev) => {
        const next = new Map(prev);
        const existing = next.get(from);
        const identity = parseIdentityNickname(fromNickname || "");
        const participant: RemoteParticipant = existing
          ? { ...existing }
          : {
              id: from,
              displayName: identity.displayName,
              avatarUrl: identity.avatarUrl,
              lodosUserId: identity.lodosUserId,
              isAudioEnabled: true,
              isVideoEnabled: true,
              isScreenSharing: false,
              screenShareMid: null,
              isHandRaised: false,
            };

        if (!participant.displayName) participant.displayName = identity.displayName;
        if (!participant.avatarUrl && identity.avatarUrl) participant.avatarUrl = identity.avatarUrl;
        if (!participant.lodosUserId && identity.lodosUserId) participant.lodosUserId = identity.lodosUserId;

        participant.isScreenSharing = payload.enabled;
        if (!payload.enabled) {
          participant.screenShareMid = null;
        } else if (payload.mid) {
          participant.screenShareMid = payload.mid;
        }

        next.set(from, participant);
        return next;
      });

      return;
    }

    if (signalType === "screen-share-state-request") {
      sendLocalScreenShareState(from);
      return;
    }

    let pc = peerConnectionsRef.current.get(from);

    if (signalType === "offer") {
      console.log("Handling offer from:", from);
      // If we already have a PC for this peer and it is in a failed or
      // closed state, tear it down first so createPeerConnection below
      // creates a fresh one to handle this offer (the remote likely just
      // rebuilt their side for the same reason).
      if (pc) {
        const stale =
          pc.connectionState === "failed" ||
          pc.connectionState === "closed" ||
          pc.iceConnectionState === "failed" ||
          pc.iceConnectionState === "closed";
        if (stale) {
          console.log("Tearing down stale PC before handling new offer from:", from, pc.connectionState, pc.iceConnectionState);
          try { pc.close(); } catch { /* ignore */ }
          clearPeerRuntimeMappings(from);
          peerConnectionsRef.current.delete(from);
          pendingCandidatesRef.current.delete(from);
          const staleChat = chatChannelsRef.current.get(from);
          if (staleChat) {
            try { staleChat.close(); } catch { /* ignore */ }
            chatChannelsRef.current.delete(from);
          }
          pc = undefined;
        }
      }
      pc = await createPeerConnection(from, false);

      // Perfect Negotiation: handle glare (simultaneous offers)
      const offerCollision = pc.signalingState === "have-local-offer" || pc.signalingState === "have-local-pranswer";
      if (offerCollision) {
        const isImpolite = shouldCreateOffer(localUserIdRef.current, from);
        if (isImpolite) {
          console.log("Glare detected, ignoring offer from:", from, "(we are impolite, our offer wins)");
          return;
        }
        console.log("Glare detected, rolling back to accept offer from:", from, "(we are polite)");
        try {
          await pc.setLocalDescription({ type: "rollback" } as RTCSessionDescriptionInit);
        } catch (error) {
          console.error("Failed to rollback local description:", error);
          return;
        }
      }

      try {
        await pc.setRemoteDescription(data as RTCSessionDescriptionInit);
      } catch (error) {
        console.error("Failed to set remote offer:", error);
        return;
      }

      await processPendingCandidates(from, pc);

      try {
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        console.log("Sending answer to:", from);
        sendSignal(from, "answer", answer);
        if (isScreenSharing) {
          sendLocalScreenShareState(from);
        }
      } catch (error) {
        console.error("Failed to create/set local answer:", error);
      }
    } else if (signalType === "answer") {
      console.log("Handling answer from:", from);
      if (pc) {
        if (pc.signalingState !== "have-local-offer") {
          console.warn("Ignoring answer in signalingState:", pc.signalingState, "from:", from);
          return;
        }

        if (pc.currentRemoteDescription?.type === "answer") return;

        try {
          await pc.setRemoteDescription(data as RTCSessionDescriptionInit);
        } catch (error) {
          console.error("Failed to set remote answer:", error);
          return;
        }

        await processPendingCandidates(from, pc);
        if (isScreenSharing) {
          sendLocalScreenShareState(from);
        }
      } else {
        console.warn("No peer connection found for answer from:", from);
      }
    } else if (signalType === "ice-candidate") {
      if (pc && pc.remoteDescription && pc.signalingState !== "have-local-offer") {
        try {
          await pc.addIceCandidate(data as RTCIceCandidateInit);
        } catch (err) {
          console.error("Failed to add ICE candidate:", err);
        }
      } else {
        const pending = pendingCandidatesRef.current.get(from) || [];
        pending.push(data as RTCIceCandidateInit);
        pendingCandidatesRef.current.set(from, pending);
      }
    }
  }

  function closePeerConnection(odaHakları: string) {
    clearPeerRuntimeMappings(odaHakları);

    const chatChannel = chatChannelsRef.current.get(odaHakları);
    if (chatChannel) {
      try {
        chatChannel.close();
      } catch {
        // ignore
      }
      chatChannelsRef.current.delete(odaHakları);
    }

    const pc = peerConnectionsRef.current.get(odaHakları);
    if (pc) {
      pc.close();
      peerConnectionsRef.current.delete(odaHakları);
    }
  }

  const joinCall = useCallback(async () => {
    try {
      setCallStatus("connecting");
      meetingStartedAtRef.current = Date.now();
      if (!localStreamRef.current) {
        try {
          await startStream({ audio: true, video: true });
        } catch (error) {
          console.error("Failed to start local media:", error);
        }
      }
      connect(token);
    } catch (error) {
      console.error("Failed to start call:", error);
      setCallStatus("idle");
    }
  }, [startStream, connect, token]);

  function handleEndCall() {
    setCallStatus("ended");
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("memeet:call-status", { detail: { inCall: false } }));
      }
    } catch {
      // ignore
    }
    stopScreenShare();

    peerConnectionsRef.current.forEach((_, odaHakları) => {
      closePeerConnection(odaHakları);
    });

    chatChannelsRef.current.forEach((channel) => {
      try {
        channel.close();
      } catch {
        // ignore
      }
    });
    chatChannelsRef.current.clear();
    chatMessageIdsRef.current.clear();
    setChatMessages([]);
    setChatDraft("");
    setIsChatOpen(false);
    setUnreadChatCount(0);
    setChatContextMenu(null);
    setIsChecklistPickerOpen(false);
    setChecklistPickerMessageId(null);
    setChecklistActionError("");
    setAddingChecklistForMessageId(null);
    setIsAddingToNotesForMessageId(null);
    setChecklistAddsByMessageId({});
    setNotesAddedByMessageId({});
    meetingStartedAtRef.current = Date.now();

    leaveCall();
    leaveRoom();
    stopStream();
    setRemoteParticipants(new Map());
    setRemoteStreams(new Map());
    setCallDuration(0);
    if (autoLowerHandTimeoutRef.current) {
      window.clearTimeout(autoLowerHandTimeoutRef.current);
      autoLowerHandTimeoutRef.current = null;
    }
    if (autoLowerHandSilenceTimeoutRef.current) {
      window.clearTimeout(autoLowerHandSilenceTimeoutRef.current);
      autoLowerHandSilenceTimeoutRef.current = null;
    }
    localHandRaisedRef.current = false;
    setIsHandRaised(false);
    hasSentInitialMediaStateRef.current = false;
    onCallEnd?.();

    setTimeout(() => setCallStatus("idle"), 1500);
  }

  const endCall = useCallback(() => {
    handleEndCall();
  }, [leaveCall, leaveRoom, stopStream, onCallEnd]);

  const requestEndCall = useCallback(() => {
    if (typeof window === "undefined") {
      endCall();
      return;
    }

    try {
      const requestEvent = new CustomEvent("memeet:request-end-call", { cancelable: true });
      const handledByExternalConfirm = !window.dispatchEvent(requestEvent);
      if (!handledByExternalConfirm) {
        endCall();
      }
    } catch {
      endCall();
    }
  }, [endCall]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const handler = () => {
        endCall();
      };
      window.addEventListener("memeet:confirm-end-call", handler);
      return () => window.removeEventListener("memeet:confirm-end-call", handler);
    } catch {
      return;
    }
  }, [endCall]);

  const stopScreenShare = useCallback(() => {
    debugLog("screen-share-stop-request", {
      callStatus,
      peerCount: peerConnectionsRef.current.size,
      hadStream: Boolean(screenStreamRef.current),
    });
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    peerConnectionsRef.current.forEach((pc, peerId) => {
      const localCameraTrack = localStreamRef.current?.getVideoTracks?.()?.[0] ?? null;
      const { screenSender } = resolveVideoSenderSlotsForPeer(peerId, pc, localCameraTrack);
      if (screenSender) {
        screenSender.replaceTrack(null);
        debugLog("screen-share-sender-cleared", {
          peerId,
          senderSummary: describePeerVideoSenders(pc, localCameraTrack),
        });
      } else {
        debugLog("screen-share-sender-missing", { peerId });
      }
    });

    setIsScreenSharing(false);
    broadcastScreenShareState(false);
  }, [broadcastScreenShareState, callStatus, resolveVideoSenderSlotsForPeer]);

  const startScreenShare = useCallback(async () => {
    try {
      debugLog("screen-share-start-request", {
        callStatus,
        isVideoEnabled,
        peerCount: peerConnectionsRef.current.size,
      });
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      screenStreamRef.current = screenStream;
      const screenTrack = screenStream.getVideoTracks()[0];
      debugLog("screen-share-track-ready", {
        trackId: screenTrack?.id ?? null,
        label: screenTrack?.label ?? null,
        readyState: screenTrack?.readyState ?? null,
      });

      const replaceOperations: Array<Promise<unknown>> = [];
      const renegotiateOperations: Array<Promise<unknown>> = [];
      peerConnectionsRef.current.forEach((pc, peerId) => {
        const localCameraTrack = localStreamRef.current?.getVideoTracks?.()?.[0] ?? null;
        const { screenSender } = resolveVideoSenderSlotsForPeer(peerId, pc, localCameraTrack, {
          ensureTwoSlots: true,
        });
        if (!screenSender) {
          debugLog("screen-share-sender-missing-before-attach", {
            peerId,
            senderSummary: describePeerVideoSenders(pc, localCameraTrack),
          });
          renegotiateOperations.push(renegotiatePeerConnection(peerId, pc, "missing-screen-sender"));
          return;
        }
        const screenSenderMid = getSenderMid(pc, screenSender);
        try {
          const result = screenSender.replaceTrack(screenTrack);
          debugLog("screen-share-track-attached", {
            peerId,
            screenSenderMid: screenSenderMid ?? null,
            senderSummary: describePeerVideoSenders(pc, localCameraTrack),
          });
          if (result && typeof (result as Promise<unknown>).then === "function") {
            replaceOperations.push(result);
          }
          if (!screenSenderMid) {
            debugLog("screen-share-sender-without-mid", {
              peerId,
              senderSummary: describePeerVideoSenders(pc, localCameraTrack),
            });
            renegotiateOperations.push(renegotiatePeerConnection(peerId, pc, "screen-sender-without-mid"));
          }
        } catch (error) {
          console.warn("Failed to replace video track for screen share:", error);
          debugLog("screen-share-track-attach-error", {
            peerId,
            message: error instanceof Error ? error.message : String(error),
          });
        }
      });
      if (replaceOperations.length > 0) {
        await Promise.all(replaceOperations.map((p) => p.catch(() => undefined)));
      }
      if (renegotiateOperations.length > 0) {
        await Promise.all(renegotiateOperations.map((p) => p.catch(() => undefined)));
      }

      screenTrack.onended = () => {
        stopScreenShare();
      };

      setIsScreenSharing(true);
      broadcastScreenShareState(true);
      debugLog("screen-share-started", { peerCount: peerConnectionsRef.current.size });
    } catch (error) {
      console.error("Failed to start screen share:", error);
      debugLog("screen-share-start-error", { message: error instanceof Error ? error.message : String(error) });
    }
  }, [
    broadcastScreenShareState,
    stopScreenShare,
    callStatus,
    isVideoEnabled,
    resolveVideoSenderSlotsForPeer,
    renegotiatePeerConnection,
  ]);

  const toggleScreenShare = useCallback(() => {
    if (isScreenSharing) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  }, [isScreenSharing, startScreenShare, stopScreenShare]);

  const broadcastHandState = useCallback(
    (raised: boolean) => {
      remoteParticipantsRef.current.forEach((_, participantId) => {
        sendSignal(participantId, "hand-state", { raised });
      });
    },
    [sendSignal]
  );

  const clearAutoLowerHandTimeout = useCallback(() => {
    if (!autoLowerHandTimeoutRef.current) return;
    window.clearTimeout(autoLowerHandTimeoutRef.current);
    autoLowerHandTimeoutRef.current = null;
  }, []);

  const clearAutoLowerHandSilenceTimeout = useCallback(() => {
    if (!autoLowerHandSilenceTimeoutRef.current) return;
    window.clearTimeout(autoLowerHandSilenceTimeoutRef.current);
    autoLowerHandSilenceTimeoutRef.current = null;
  }, []);

  const lowerLocalHandIfRaised = useCallback(() => {
    setIsHandRaised((prev) => {
      if (!prev) return prev;
      localHandRaisedRef.current = false;
      broadcastHandState(false);
      return false;
    });
  }, [broadcastHandState]);

  const handleToggleHandRaise = useCallback(() => {
    setIsHandRaised((prev) => {
      const nextRaised = !prev;
      if (!nextRaised) {
        clearAutoLowerHandTimeout();
        clearAutoLowerHandSilenceTimeout();
      }
      localHandRaisedRef.current = nextRaised;
      broadcastHandState(nextRaised);
      if (nextRaised) playHandRaiseChime();
      return nextRaised;
    });
  }, [broadcastHandState, clearAutoLowerHandTimeout, clearAutoLowerHandSilenceTimeout, playHandRaiseChime]);

  useEffect(() => {
    const canTrackSpeech = callStatus === "in-call" && isHandRaised && isAudioEnabled;
    const isSpeakingNow = canTrackSpeech && audioLevel > HAND_AUTO_LOWER_SPEAKING_THRESHOLD;

    if (!canTrackSpeech) {
      clearAutoLowerHandTimeout();
      clearAutoLowerHandSilenceTimeout();
      return;
    }

    if (isSpeakingNow) {
      clearAutoLowerHandSilenceTimeout();
      if (autoLowerHandTimeoutRef.current) return;

      autoLowerHandTimeoutRef.current = window.setTimeout(() => {
        autoLowerHandTimeoutRef.current = null;
        clearAutoLowerHandSilenceTimeout();
        lowerLocalHandIfRaised();
      }, HAND_AUTO_LOWER_DELAY_MS);
      return;
    }

    if (!autoLowerHandTimeoutRef.current || autoLowerHandSilenceTimeoutRef.current) return;

    autoLowerHandSilenceTimeoutRef.current = window.setTimeout(() => {
      autoLowerHandSilenceTimeoutRef.current = null;
      clearAutoLowerHandTimeout();
    }, HAND_AUTO_LOWER_SILENCE_GRACE_MS);
  }, [
    callStatus,
    isHandRaised,
    isAudioEnabled,
    audioLevel,
    clearAutoLowerHandTimeout,
    clearAutoLowerHandSilenceTimeout,
    lowerLocalHandIfRaised,
  ]);

  useEffect(() => {
    return () => {
      clearAutoLowerHandTimeout();
      clearAutoLowerHandSilenceTimeout();
    };
  }, [clearAutoLowerHandTimeout, clearAutoLowerHandSilenceTimeout]);

  const handleToggleAudio = useCallback(() => {
    const nextEnabled = !isAudioEnabled;
    localMediaStateRef.current.audioEnabled = nextEnabled;
    toggleAudio();
    if (callStatus === "in-call") {
      toggleMedia("audio", nextEnabled);
      broadcastLocalMediaState();
    }
  }, [toggleAudio, toggleMedia, isAudioEnabled, callStatus, broadcastLocalMediaState]);

  const handleToggleVideo = useCallback(() => {
    (async () => {
      const nextEnabled = await toggleVideo();
      localMediaStateRef.current.videoEnabled = nextEnabled;

      if (callStatus === "in-call") {
        toggleMedia("video", nextEnabled);
        broadcastLocalMediaState();

        const activeTrack = localStreamRef.current?.getVideoTracks()?.[0] ?? null;
        peerConnectionsRef.current.forEach((pc, peerId) => {
          const { cameraSender } = resolveVideoSenderSlotsForPeer(peerId, pc, activeTrack, {
            ensureTwoSlots: true,
          });
          if (cameraSender) {
            cameraSender.replaceTrack(activeTrack);
            debugLog("camera-track-updated", {
              peerId,
              nextEnabled,
              activeTrackId: activeTrack?.id ?? null,
              activeTrackState: activeTrack?.readyState ?? null,
              senderSummary: describePeerVideoSenders(pc, activeTrack),
            });
          } else {
            debugLog("camera-sender-missing", {
              peerId,
              nextEnabled,
              activeTrackId: activeTrack?.id ?? null,
            });
          }
        });
      }

      debugLog("toggle-video-result", {
        nextEnabled,
        callStatus,
        isScreenSharing,
        localTracks: describeMediaStreamTracks(localStreamRef.current),
      });
    })().catch((error) => {
      console.error("Failed to toggle video:", error);
      debugLog("toggle-video-error", { message: error instanceof Error ? error.message : String(error) });
    });
  }, [toggleVideo, toggleMedia, callStatus, broadcastLocalMediaState, isScreenSharing, resolveVideoSenderSlotsForPeer]);

  // Join room and start call when socket is connected
  useEffect(() => {
    if (socketStatus === "connected" && callStatus === "connecting") {
      setNickname(encodedNickname);

      if (!localUserId) return;

      let cancelled = false;
      (async () => {
        // Wait for join-room ACK before issuing call-start. Without this,
        // the server can receive call-start before the user is registered in
        // the room, which causes them to be missed by later joiners.
        if (socketRoomId !== roomId) {
          await new Promise<void>((resolve) => {
            let settled = false;
            const finish = () => {
              if (settled) return;
              settled = true;
              resolve();
            };
            // Safety timeout — don't block forever if server never acks
            const timeout = window.setTimeout(() => {
              console.warn("[VideoCall] join-room ACK timeout — proceeding anyway");
              finish();
            }, 5000);
            joinRoom(roomId, (ackData) => {
              window.clearTimeout(timeout);
              console.log("[VideoCall] join-room ACK received", ackData);
              finish();
            });
          });
          if (cancelled) return;
        }

        const result = await socketStartCall(roomId);
        if (cancelled) return;
        if (result) {
          console.log("Call started with participants:", result.participants);
          for (const p of result.participants) {
            if (!p.odaHakları) continue;
            if (localUserId && p.odaHakları === localUserId) continue;
            const identity = parseIdentityNickname(p.nickname);
            const participant: RemoteParticipant = {
              id: p.odaHakları,
              displayName: identity.displayName,
              avatarUrl: identity.avatarUrl,
              lodosUserId: identity.lodosUserId,
              isAudioEnabled: true,
              isVideoEnabled: true,
              isScreenSharing: false,
              screenShareMid: null,
              isHandRaised: false,
            };

            setRemoteParticipants((prev) => new Map(prev).set(participant.id, participant));
            requestRemoteMediaState(participant.id);
            sendLocalMediaState(participant.id);
            requestRemoteScreenShareState(participant.id);
            sendLocalScreenShareState(participant.id);
            requestRemoteHandState(participant.id);
            sendLocalHandState(participant.id);

            if (!localStreamRef.current || !localUserIdRef.current) {
              queuePendingUser(participant);
              continue;
            }

            try {
              await createPeerConnection(participant.id, shouldCreateOffer(localUserIdRef.current, participant.id));
            } catch (err) {
              console.error("Failed to create peer connection for roster participant:", participant.id, err);
            }
          }
          setCallStatus("in-call");
        }
      })();

      return () => {
        cancelled = true;
      };
    }
  }, [
    socketStatus,
    callStatus,
    socketRoomId,
    roomId,
    encodedNickname,
    setNickname,
    joinRoom,
    socketStartCall,
    localUserId,
    requestRemoteMediaState,
    sendLocalMediaState,
    requestRemoteScreenShareState,
    sendLocalScreenShareState,
    requestRemoteHandState,
    sendLocalHandState,
    queuePendingUser,
  ]);

  // Call duration timer
  useEffect(() => {
    let interval: number | undefined;

    if (callStatus === "in-call") {
      interval = window.setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callStatus]);

  // Device change recovery — restore local audio track after iPhone call handoff / AirPods switch
  useEffect(() => {
    if (callStatus !== "in-call") return;

    let running = false;
    let muteRecoveryTimer: number | undefined;
    let healthCheckInterval: number | undefined;
    let retryTimer: number | undefined;
    let disposed = false;

    const replaceAudioTracks = async (reason: string, retryCount = 0) => {
      if (running || disposed) return;
      const stream = localStreamRef.current;
      if (!stream) return;
      const deadTracks = stream.getAudioTracks().filter(
        (t) => t.readyState === "ended" || t.muted
      );
      if (deadTracks.length === 0) return;

      running = true;
      console.log(`[AudioRecovery] Attempting recovery (reason: ${reason}, retry: ${retryCount})`);
      try {
        const prevEnabled = deadTracks.some((t) => t.enabled);
        const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const newTrack = newStream.getAudioTracks()[0];
        if (newTrack && !disposed) {
          newTrack.enabled = prevEnabled;
          peerConnectionsRef.current.forEach((pc) => {
            const sender = pc.getSenders().find((s) => s.track?.kind === "audio");
            if (sender) sender.replaceTrack(newTrack).catch(() => {});
          });
          deadTracks.forEach((t) => { try { stream.removeTrack(t); } catch { /* ignore */ } });
          stream.addTrack(newTrack);
          attachTrackListeners(newTrack);
          console.log("[AudioRecovery] Audio track replaced successfully");
        }
      } catch (err) {
        console.warn("[AudioRecovery] getUserMedia failed, will retry:", err);
        if (retryCount < 3 && !disposed) {
          const delay = Math.min(2000 * Math.pow(2, retryCount), 8000);
          retryTimer = window.setTimeout(() => replaceAudioTracks(reason, retryCount + 1), delay);
        }
      }
      running = false;
    };

    const attachTrackListeners = (track: MediaStreamTrack) => {
      track.addEventListener("ended", () => {
        console.log("[AudioRecovery] Local audio track ended");
        clearTimeout(muteRecoveryTimer);
        replaceAudioTracks("track-ended");
      });
      track.addEventListener("mute", () => {
        console.log("[AudioRecovery] Local audio track muted");
        clearTimeout(muteRecoveryTimer);
        muteRecoveryTimer = window.setTimeout(() => {
          const s = localStreamRef.current;
          if (!s) return;
          const stillDead = s.getAudioTracks().filter((t) => t.muted || t.readyState === "ended");
          if (stillDead.length > 0) replaceAudioTracks("track-mute-timeout");
        }, 2000);
      });
      track.addEventListener("unmute", () => {
        clearTimeout(muteRecoveryTimer);
      });
    };

    const stream = localStreamRef.current;
    if (stream) {
      stream.getAudioTracks().forEach(attachTrackListeners);
    }

    const handleDeviceChange = () => {
      const s = localStreamRef.current;
      if (!s) return;
      const endedTracks = s.getAudioTracks().filter((t) => t.readyState === "ended");
      if (endedTracks.length > 0) {
        clearTimeout(muteRecoveryTimer);
        replaceAudioTracks("devicechange-ended");
        return;
      }
      const mutedTracks = s.getAudioTracks().filter((t) => t.muted);
      if (mutedTracks.length > 0) {
        clearTimeout(muteRecoveryTimer);
        muteRecoveryTimer = window.setTimeout(() => {
          const current = localStreamRef.current;
          if (!current) return;
          const stillMuted = current.getAudioTracks().filter((t) => t.muted && t.readyState !== "ended");
          if (stillMuted.length > 0) replaceAudioTracks("devicechange-mute-timeout");
        }, 2000);
      }
    };

    healthCheckInterval = window.setInterval(() => {
      const s = localStreamRef.current;
      if (!s) return;
      const audioTracks = s.getAudioTracks();
      const hasDead = audioTracks.length === 0 || audioTracks.some((t) => t.readyState === "ended" || t.muted);
      if (hasDead) replaceAudioTracks("health-check");
    }, 5000);

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      disposed = true;
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
      clearTimeout(muteRecoveryTimer);
      clearTimeout(retryTimer);
      clearInterval(healthCheckInterval);
    };
  }, [callStatus]);

  useEffect(() => {
    if (callStatus !== "in-call" || !callId || hasSentInitialMediaStateRef.current) return;
    hasSentInitialMediaStateRef.current = true;
    toggleMedia("audio", isAudioEnabled);
    toggleMedia("video", isVideoEnabled);
  }, [callStatus, callId, toggleMedia, isAudioEnabled, isVideoEnabled]);

  const gridParticipants = useMemo(() => {
    const remotes = Array.from(remoteParticipants.values()).flatMap((p) => {
      const stream = remoteStreams.get(p.id) || null;
      const audioTracks = stream?.getAudioTracks?.() || [];
      const videoTracks = stream?.getVideoTracks?.() || [];

      // If a remote stream ever carries multiple video tracks (camera + screen),
      // split them into separate tiles so both can be shown.
      if (p.isScreenSharing && videoTracks.length > 1) {
        const screenTrackId = p.screenShareMid ? getRemoteTrackIdByMid(p.id, p.screenShareMid) : null;
        const screenTrack =
          (screenTrackId ? videoTracks.find((track) => track.id === screenTrackId) : undefined) ??
          pickLikelyScreenTrack(videoTracks) ??
          null;
        const cameraTrack = videoTracks.find((track) => track !== screenTrack) ?? null;

        const camStream = new MediaStream();
        if (cameraTrack) camStream.addTrack(cameraTrack);
        if (audioTracks[0]) camStream.addTrack(audioTracks[0]);

        const tiles: Participant[] = [
          {
            id: `${p.id}-cam`,
            name: p.displayName,
            userId: p.lodosUserId,
            stream: camStream,
            avatarUrl: p.avatarUrl,
            isAudioEnabled: p.isAudioEnabled && !!audioTracks.length,
            isVideoEnabled: p.isVideoEnabled && !!cameraTrack,
            isScreenSharing: false,
            isHandRaised: p.isHandRaised,
          },
        ];

        // Only emit the screen tile when we actually have a screen track to render.
        // Otherwise we'd render an empty black tile just because of the signaled flag.
        if (screenTrack) {
          const screenStream = new MediaStream();
          screenStream.addTrack(screenTrack);
          tiles.push({
            id: `${p.id}-screen`,
            name: `${p.displayName} (${t("memeetShareScreen", "Ekranı paylaş")})`,
            userId: p.lodosUserId,
            stream: screenStream,
            avatarUrl: p.avatarUrl,
            isAudioEnabled: false,
            isVideoEnabled: true,
            isScreenSharing: true,
            isHandRaised: false,
          });
        }

        return tiles;
      }

      const hasVideoTrack = !!videoTracks.length;
      const hasAudioTrack = !!audioTracks.length;
      // Only mark the tile as "screen sharing" when we actually have a video
      // track present — otherwise we render an empty placeholder.
      const effectiveScreenSharing = p.isScreenSharing && hasVideoTrack;

      return [
        {
          id: p.id,
          name: p.displayName,
          userId: p.lodosUserId,
          stream,
          avatarUrl: p.avatarUrl,
          isAudioEnabled: p.isAudioEnabled && hasAudioTrack,
          isVideoEnabled: (p.isVideoEnabled || effectiveScreenSharing) && hasVideoTrack,
          isScreenSharing: effectiveScreenSharing,
          isHandRaised: p.isHandRaised,
        },
      ];
    });

    // Always keep local camera tile visible.
    const localCameraTile = {
      id: "local",
      name: effectiveDisplayName,
      userId: lodosUserId,
      stream: localStream || null,
      isAudioEnabled,
      isVideoEnabled,
      isScreenSharing: false,
      isHandRaised,
      isLocal: true,
      avatarUrl,
    };

    // If local is sharing screen AND we actually have a live video track,
    // add an extra tile for the shared screen stream. Without the track
    // check the UI would show an empty black tile while the user is
    // starting/stopping screen share.
    const localScreenStream = screenStreamRef.current;
    const hasLocalScreenVideoTrack = !!localScreenStream?.getVideoTracks?.().length;
    const localScreenTile = isScreenSharing && hasLocalScreenVideoTrack
      ? [
          {
            id: "local-screen",
            name: `${effectiveDisplayName} (${t("memeetShareScreen", "Ekranı paylaş")})`,
            userId: lodosUserId,
            stream: localScreenStream || null,
            isAudioEnabled: false,
            isVideoEnabled: true,
            isScreenSharing: true,
            isHandRaised: false,
            isLocal: true,
            avatarUrl,
          },
        ]
      : [];

    return [...remotes, ...localScreenTile, localCameraTile];
  }, [
    remoteParticipants,
    remoteStreams,
    localStream,
    isAudioEnabled,
    isVideoEnabled,
    isHandRaised,
    effectiveDisplayName,
    avatarUrl,
    isScreenSharing,
    t,
    lodosUserId,
    getRemoteTrackIdByMid,
  ]);

  const formatDuration = (seconds: number) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusText = () => {
    switch (callStatus) {
      case "idle":
        return t("memeetReady", "Hazır");
      case "connecting":
        return t("memeetConnecting", "Bağlanıyor...");
      case "in-call":
        return formatDuration(callDuration);
      case "ended":
        return t("memeetCallEnded", "Görüşme bitti");
      default:
        return "";
    }
  };

  const sharePath = useMemo(() => `/memeet/${encodeURIComponent(roomId)}`, [roomId]);

  const shareUrl = useMemo(() => {
    try {
      if (getShareUrl) return String(getShareUrl(roomId) || "");
    } catch {
      // ignore
    }
    try {
      if (typeof window === "undefined") return sharePath;
      return `${window.location.origin}${sharePath}`;
    } catch {
      return sharePath;
    }
  }, [getShareUrl, roomId, sharePath]);

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyRoomId = useCallback(async () => {
    // navigator.clipboard güvenli-bağlam yoksa tanımsız/reject olabilir →
    // execCommand textarea fallback'i ekle; "copied" sadece başarıda gösterilsin.
    const text = shareUrl || roomId;
    if (!text) return;
    let ok = false;
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function" &&
        (typeof window === "undefined" || window.isSecureContext !== false)
      ) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      // fallback'e düş
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.width = "1px";
        ta.style.height = "1px";
        ta.style.opacity = "0";
        ta.style.pointerEvents = "none";
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, text.length);
        ok = !!(document.execCommand && document.execCommand("copy"));
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  }, [roomId, shareUrl]);

  const handleBack = useCallback(() => {
    if (callStatus === "idle" || callStatus === "ended" || callStatus === "connecting") {
      stopStream();
      onExitRoom?.();
      return;
    }
    endCall();
  }, [callStatus, onExitRoom, endCall, stopStream]);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, []);

  const prejoinParticipants = useMemo(() => {
    return Array.from(remoteParticipants.values())
      .filter((p) => p.id && p.displayName)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [remoteParticipants]);
  const prejoinVisibleParticipants = useMemo(
    () => prejoinParticipants.slice(0, MAX_PREJOIN_PARTICIPANTS),
    [prejoinParticipants]
  );
  const prejoinExtraCount = prejoinParticipants.length - prejoinVisibleParticipants.length;

  const sendChatMessage = useCallback(() => {
    const trimmed = String(chatDraft || "").trim();
    if (!trimmed && !pendingAttachment) return;

    const now = Date.now();
    const message: ChatMessage = {
      id: makeChatId(),
      text: trimmed,
      ts: now,
      fromId: localUserId || "local",
      fromName: effectiveDisplayName,
      fromAvatarUrl: avatarUrl,
      isLocal: true,
      attachment: pendingAttachment || undefined,
    };

    appendChatMessage(message);
    setChatDraft("");
    setPendingAttachment(null);

    const wireMessage: ChatWireMessageV1 = {
      v: 1,
      type: "chat-message",
      id: message.id,
      ts: message.ts,
      text: message.text,
      from: {
        name: message.fromName,
        avatarUrl: message.fromAvatarUrl,
      },
      attachment: message.attachment,
    };

    const payload = JSON.stringify(wireMessage);
    chatChannelsRef.current.forEach((channel) => {
      if (channel.readyState !== "open") return;
      try {
        channel.send(payload);
      } catch (error) {
        console.error("Failed to send chat message:", error);
      }
    });
  }, [appendChatMessage, avatarUrl, chatDraft, effectiveDisplayName, localUserId, pendingAttachment]);

  const handleChatSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      sendChatMessage();
    },
    [sendChatMessage]
  );

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }, []);

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;
          if (file.size > MAX_ATTACHMENT_SIZE) {
            alert(t("memeetFileTooLarge", "Dosya \u00e7ok b\u00fcy\u00fck. Maksimum 2MB."));
            return;
          }
          const base64 = await fileToBase64(file);
          setPendingAttachment({
            type: file.type,
            name: file.name || `image-${Date.now()}.png`,
            data: base64,
            size: file.size,
          });
          break;
        }
      }
    },
    [fileToBase64, t]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_ATTACHMENT_SIZE) {
        alert(t("memeetFileTooLarge", "Dosya \u00e7ok b\u00fcy\u00fck. Maksimum 2MB."));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      const base64 = await fileToBase64(file);
      setPendingAttachment({
        type: file.type,
        name: file.name,
        data: base64,
        size: file.size,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [fileToBase64, t]
  );

  const handleRemoveAttachment = useCallback(() => {
    setPendingAttachment(null);
  }, []);

  const formatChatTime = useCallback(
    (ts: number) => {
      try {
        return new Date(ts).toLocaleTimeString(language || undefined, {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return "";
      }
    },
    [language]
  );

  return (
    <div className="memeet-video-call">
      {callStatus === "idle" || callStatus === "connecting" ? (
        <div className="memeet-prejoin">
          <div className="prejoin-preview">
            {localStream && isVideoEnabled ? (
              <video ref={previewVideoRef} autoPlay playsInline muted className="prejoin-video" />
            ) : (
              <div className="prejoin-avatar">
                <AvatarCircle name={effectiveDisplayName} avatarUrl={avatarUrl} />
              </div>
            )}
            <div className="tile-name-badge">{effectiveDisplayName}</div>
          </div>

          <div className="prejoin-panel">
            <h2>
              {callStatus === "connecting"
                ? t("memeetConnecting", "Bağlanıyor...")
                : t("memeetReadyToJoin", "Katılmaya hazır mısınız?")}
            </h2>
            <p>{t("memeetJoinWithoutVideoHint", "Kameranız kapalıysa toplantıya görüntüsüz katılabilirsiniz.")}</p>

            {prejoinParticipants.length > 0 && (
              <div className="prejoin-participants">
                <span className="prejoin-participants-label">{t("memeetPeopleInside", "İçeridekiler")}</span>
                <div className="prejoin-participants-row">
                  <div className="prejoin-participants-stack">
                    {prejoinVisibleParticipants.map((participant) => (
                      <div
                        key={participant.id}
                        className="prejoin-participant-avatar"
                        title={participant.displayName}
                        aria-label={participant.displayName}
                      >
                        <AvatarCircle name={participant.displayName} avatarUrl={participant.avatarUrl} />
                      </div>
                    ))}
                  </div>

                  {prejoinExtraCount > 0 && (
                    <span className="prejoin-participants-more">
                      {t("memeetMorePeople", `${prejoinExtraCount} Kişi daha`, {
                        count: String(prejoinExtraCount),
                      })}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="prejoin-controls">
              <button
                className={`icon-button large ${!isAudioEnabled ? "off" : ""}`}
                onClick={handleToggleAudio}
                title={
                  isAudioEnabled
                    ? t("memeetTurnOffMic", "Mikrofonu kapat")
                    : t("memeetTurnOnMic", "Mikrofonu aç")
                }
              >
                {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
              </button>

              <button
                className={`icon-button large ${!isVideoEnabled ? "off" : ""}`}
                onClick={handleToggleVideo}
                title={
                  isVideoEnabled
                    ? t("memeetTurnOffCamera", "Kamerayı kapat")
                    : t("memeetTurnOnCamera", "Kamerayı aç")
                }
              >
                {isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
              </button>
            </div>

            <div className="prejoin-actions">
              <button className="meet-button primary" onClick={joinCall} disabled={callStatus === "connecting"}>
                {callStatus === "connecting" ? t("memeetConnecting", "Bağlanıyor...") : t("memeetJoin", "Katıl")}
              </button>
              <button className="meet-button ghost" onClick={handleBack} disabled={callStatus === "connecting"} type="button">
                {t("memeetCancel", "Vazgeç")}
              </button>
            </div>

            <div className="prejoin-status">
              <span>
                {callStatus === "connecting"
                  ? t("memeetConnectingToMeeting", "Toplantıya bağlanılıyor...")
                  : getStatusText()}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="memeet-incall">
          <div className="incall-content">
            <div className="stage">
              <ParticipantsGrid
                participants={gridParticipants}
                handRaisedLabel={t("memeetHandRaised", "El kaldırdı")}
                pinLabel={t("memeetFullscreen", "Tam ekran")}
                unpinLabel={t("memeetExitFullscreen", "Tam ekrandan çık")}
                getOverflowLabel={(count) =>
                  count === 1
                    ? t("memeetOverflowPerson", `${count} Kişi`, { count: String(count) })
                    : t("memeetOverflowPeople", `${count} Kişi`, { count: String(count) })
                }
                participantActionLabel={
                  onSendDmOnSocialHive ? t("memeetSendDmOnSocialHive", "Send DM on Social Hive") : undefined
                }
                onParticipantActionClick={onSendDmOnSocialHive ? handleParticipantActionClick : undefined}
              />
            </div>

            <aside className={`memeet-soundboard-panel ${isSoundboardOpen ? "open" : ""}`} aria-hidden={!isSoundboardOpen}>
              <div className="soundboard-panel-header">
                <span className="soundboard-panel-title">{t("memeetSoundboard", "Soundboard")}</span>
                <button
                  className="icon-button chat-close"
                  onClick={() => setIsSoundboardOpen(false)}
                  title={t("memeetCloseSoundboard", "Kapat")}
                  type="button"
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="soundboard-grid">
                {(soundboardConfig.sounds as SoundboardItem[]).map((sound) => (
                  <button
                    key={sound.id}
                    className={`soundboard-btn ${playingSoundId === sound.id ? "playing" : ""}`}
                    onClick={() => handleSoundboardClick(sound.id)}
                    disabled={callStatus !== "in-call"}
                    title={sound.title}
                    type="button"
                  >
                    <SoundboardIcon icon={sound.icon} />
                    <span className="soundboard-btn-label">{sound.title}</span>
                    {playingSoundId === sound.id && <span className="soundboard-playing-indicator" />}
                  </button>
                ))}
              </div>
            </aside>

            <aside className={`memeet-chat-panel ${isChatOpen ? "open" : ""}`} aria-hidden={!isChatOpen}>
              <div className="chat-panel-header">
                <span className="chat-panel-title">{t("memeetChat", "Sohbet")}</span>
                <button
                  className="icon-button chat-close"
                  onClick={() => setIsChatOpen(false)}
                  title={t("memeetCloseChat", "Sohbeti kapat")}
                  type="button"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="chat-messages" ref={chatListRef} onScroll={updateChatScrollLock}>
                {chatMessages.length === 0 ? (
                  <div className="chat-empty">{t("memeetChatEmpty", "Henüz mesaj yok.")}</div>
                ) : (
                  chatMessages.map((m) => {
                    const checklistAdds = (checklistAddsByMessageId[m.id] || []).filter((a) =>
                      workspaceIdSet.has(String(a.workspaceId))
                    );
                    const notesAdd = notesAddedByMessageId[m.id] || null;
                    const preview = truncateChatPreview(m.text);

                    return (
                    <div key={m.id} className={`chat-message ${m.isLocal ? "local" : ""}`}>
                      <div className="chat-avatar">
                        {m.fromAvatarUrl ? (
                          <img src={m.fromAvatarUrl} alt={m.fromName} />
                        ) : (
                          <span>{getInitials(m.fromName)}</span>
                        )}
                      </div>

                      <div className="chat-content">
                        <div className="chat-bubble" onContextMenu={(e) => openChatContextMenu(e, m.id)}>
                          <div className="chat-meta">
                            <span className="chat-name">{m.isLocal ? t("memeetYou", "Siz") : m.fromName}</span>
                            <div className="chat-meta-right">
                              {!m.attachment && (
                                <>
                                  <button
                                    type="button"
                                    className="memeet-chat-action-btn"
                                    title={t("memeetAddToChecklist", "Add to checklist")}
                                    aria-label={t("memeetAddToChecklist", "Add to checklist")}
                                    disabled={!canAddToChecklist || isChecklistAddInFlight}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      openChecklistPickerForMessage(m.id);
                                    }}
                                  >
                                    {addingChecklistForMessageId === m.id ? (
                                      <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                    ) : (
                                      <ChecklistAddIcon />
                                    )}
                                  </button>

                                  <button
                                    type="button"
                                    className="memeet-chat-action-btn"
                                    title={t("memeetAddToNotes", "Add to notes")}
                                    aria-label={t("memeetAddToNotes", "Add to notes")}
                                    disabled={!canAddToNotes || isNotesAddInFlight}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await handleAddToNotes(m.id);
                                    }}
                                  >
                                    {isAddingToNotesForMessageId === m.id ? (
                                      <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                    ) : (
                                      <NotesAddIcon />
                                    )}
                                  </button>
                                </>
                              )}

                              <span className="chat-time">{formatChatTime(m.ts)}</span>
                            </div>
                          </div>
                          <div className="chat-text">
                            {m.attachment && (
                              <div className="chat-attachment">
                                {m.attachment.type.startsWith("image/") ? (
                                  <img
                                    src={m.attachment.data}
                                    alt={m.attachment.name}
                                    className="chat-image"
                                    onClick={() => setLightboxImage(m.attachment!.data)}
                                  />
                                ) : (
                                  <a
                                    href={m.attachment.data}
                                    download={m.attachment.name}
                                    className="chat-file-link"
                                  >
                                    <FileIcon />
                                    <span className="file-name">{m.attachment.name}</span>
                                    <span className="file-size">({formatFileSize(m.attachment.size)})</span>
                                  </a>
                                )}
                              </div>
                            )}
                            {/* Satir sonlarini BOLMUYORUZ: split("\n") bos satirlari
                                <p></p> yapiyordu, icerigi olmayan blok elemanin satir
                                kutusu olmadigi icin yuksekligi 0 olup paragraf
                                bosluklari kayboluyordu. .chat-text zaten
                                `white-space: pre-wrap` tasidigi icin metni oldugu gibi
                                verince tum satir sonlari korunuyor. Bas/son satir
                                sonlari kirpiliyor ki balonda bos bant olmasin. */}
                            {m.text && (
                              <p>{linkifyText(m.text.replace(/^\n+|\n+$/g, ""))}</p>
                            )}
                            {(() => {
                              const urls = extractUrls(m.text);
                              if (!urls.length) return null;
                              return (
                                <div className="link-preview-list">
                                  {urls.map((u) => {
                                    const pv = linkPreviews[u];
                                    if (!pv) return <LinkPreviewSkeleton key={u} />;
                                    const previewData = "error" in pv ? { title: "", description: "", image: null, url: u } : pv;
                                    const href = ("error" in pv ? u : pv.url) || u;
                                    return <LinkPreviewCard key={u} preview={previewData} href={href} getTranslation={getTranslation} />;
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {checklistAdds.map((a, idx) => (
                          <div
                            key={`${a.workspaceId}-${a.checklistId || a.checklistName}-${idx}`}
                            className="chat-action-hint"
                            title={a.checklistName}
                          >
                            <span className="chat-action-preview">
                              "{truncateChatActionToken(a.preview ? a.preview : m.text)}"
                            </span>{" "}
                            <span className="chat-action-text">
                              {t(
                                "memeetWasAddedIntoChecklistNamed",
                                `was added into checklist named ${a.checklistName}`,
                                { checklistName: a.checklistName }
                              )}
                            </span>
                          </div>
                        ))}

                        {notesAdd ? (
                          <div className="chat-action-hint">
                            <span className="chat-action-preview">"{truncateChatActionToken(m.text)}"</span>{" "}
                            <span className="chat-action-text">
                              {t("memeetWasAddedIntoYourNotes", "was added into your notes")}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    );
                  })
                )}
              </div>

              <form className="chat-compose" onSubmit={handleChatSubmit}>
                {pendingAttachment && (
                  <div className="chat-attachment-preview">
                    {pendingAttachment.type.startsWith("image/") ? (
                      <>
                        <img src={pendingAttachment.data} alt="preview" />
                        <div className="image-info">
                          <span className="file-name">{pendingAttachment.name}</span>
                          <span className="file-meta">{formatFileSize(pendingAttachment.size)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="file-preview">
                        <FileIcon />
                        <span className="file-name">{pendingAttachment.name}</span>
                        <span className="file-size">{formatFileSize(pendingAttachment.size)}</span>
                      </div>
                    )}
                    <button type="button" className="remove-attachment" onClick={handleRemoveAttachment} title={t("memeetRemoveAttachment", "Kaldır")}>
                      <CloseIcon />
                    </button>
                  </div>
                )}
                <div className="chat-input-row">
                  <button
                    type="button"
                    className="icon-button attachment-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={callStatus !== "in-call"}
                    title={t("memeetAttachFile", "Dosya ekle")}
                  >
                    <AttachmentIcon />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={handleFileSelect}
                  />
                  <input
                    ref={chatDraftInputRef}
                    value={chatDraft}
                    onChange={(e) => setChatDraft(e.target.value)}
                    onPaste={handlePaste}
                    placeholder={t("memeetChatPlaceholder", "Mesaj yazın")}
                    disabled={callStatus !== "in-call"}
                  />
                  <button className="meet-button primary" type="submit" disabled={callStatus !== "in-call" || (!chatDraft.trim() && !pendingAttachment)}>
                    {t("memeetSend", "Gönder")}
                  </button>
                </div>
              </form>
            </aside>
          </div>

          <div className="controls-bar">
            <div className="controls-left">
              <div className="controls-left-stack">
                <button
                  type="button"
                  className="invite-room-button"
                  onClick={openInviteModal}
                  title={t("memeetInvite", "Invite")}
                >
                  <InviteIcon />
                  <span>{t("memeetInvite", "Invite")}</span>
                </button>
                {summaryApi && roomId ? (
                  <MeetingSummary summaryApi={summaryApi} roomId={roomId} t={t} />
                ) : null}
              </div>
            </div>

            <div className="controls-center">
            <button
              className={`icon-button large ${isHandRaised ? "active" : ""}`}
              onClick={handleToggleHandRaise}
              disabled={callStatus !== "in-call"}
              title={isHandRaised ? t("memeetLowerHand", "Eli indir") : t("memeetRaiseHand", "El kaldır")}
            >
              <HandRaiseIcon />
            </button>

            <button
              className={`icon-button large ${!isAudioEnabled ? "off" : ""}`}
              onClick={handleToggleAudio}
              disabled={callStatus !== "in-call"}
              title={isAudioEnabled ? t("memeetMute", "Sessize al") : t("memeetUnmute", "Sesi aç")}
            >
              {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
            </button>

            <button
              className={`icon-button large ${!isVideoEnabled ? "off" : ""}`}
              onClick={handleToggleVideo}
              disabled={callStatus !== "in-call"}
              title={
                isVideoEnabled
                  ? t("memeetTurnOffCamera", "Kamerayı kapat")
                  : t("memeetTurnOnCamera", "Kamerayı aç")
              }
            >
              {isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
            </button>

            <button
              className={`icon-button large ${isScreenSharing ? "active" : ""}`}
              onClick={toggleScreenShare}
              disabled={callStatus !== "in-call"}
              title={
                isScreenSharing
                  ? t("memeetStopSharing", "Paylaşımı durdur")
                  : t("memeetShareScreen", "Ekranı paylaş")
              }
            >
              <ScreenShareIcon />
            </button>

            <button className="icon-button large end" onClick={requestEndCall} title={t("memeetEndCall", "Aramayı bitir")}>
              <PhoneOffIcon />
            </button>
            </div>

            <div className="controls-right">
              <button
                className={`icon-button soundboard-toggle ${isSoundboardOpen ? "active" : ""}`}
                onClick={toggleSoundboard}
                title={isSoundboardOpen ? t("memeetCloseSoundboard", "Kapat") : t("memeetSoundboard", "Soundboard")}
                type="button"
              >
                <SoundboardToggleIcon />
              </button>
              <button
                className={`icon-button chat-toggle ${isChatOpen ? "active" : ""}`}
                onClick={toggleChat}
                title={isChatOpen ? t("memeetCloseChat", "Sohbeti kapat") : t("memeetOpenChat", "Sohbet")}
                type="button"
              >
                <ChatIcon />
                {unreadChatCount > 0 && (
                  <span className="memeet-chat-badge">{unreadChatCount > 9 ? "9+" : unreadChatCount}</span>
                )}
              </button>
              <span className="call-timer">{formatDuration(callDuration)}</span>
            </div>
          </div>
        </div>
      )}

      {chatContextMenu ? (
        <div
          className="memeet-chat-context-menu"
          style={{ left: chatContextMenu.x, top: chatContextMenu.y }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <button
            type="button"
            className="memeet-context-item"
            disabled={!canAddToChecklist || isChecklistAddInFlight}
            onClick={openAddToChecklistPicker}
          >
            {t("memeetAddToChecklist", "Add to checklist")}
          </button>
          <button
            type="button"
            className="memeet-context-item"
            disabled={!canAddToNotes || isNotesAddInFlight}
            onClick={async () => {
              const messageId = chatContextMenu.messageId;
              setChatContextMenu(null);
              await handleAddToNotes(messageId);
            }}
          >
            {isNotesAddInFlight ? t("memeetAdding", "Adding...") : t("memeetAddToNotes", "Add to notes")}
          </button>
        </div>
      ) : null}

      {isChecklistPickerOpen ? (
        <div
          className="memeet-modal-backdrop"
          onMouseDown={() => closeAddToChecklistPicker()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="memeet-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="memeet-modal-title">{t("memeetAddToChecklist", "Add to checklist")}</h3>

            <div className="memeet-modal-body">
              <div className="memeet-modal-row">
                <div className="memeet-modal-label">{t("memeetWorkspace", "Workspace")}</div>
                <select
                  value={selectedChecklistWorkspaceId}
                  onChange={(e) => setSelectedChecklistWorkspaceId(e.target.value)}
                  disabled={isChecklistAddInFlight}
                >
                  {resolvedWorkspaces.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.title}
                    </option>
                  ))}
                </select>
              </div>

              {(() => {
                const msg = checklistPickerMessageId ? chatMessages.find((m) => m.id === checklistPickerMessageId) : null;
                const preview = msg ? truncateChatPreview(msg.text) : "";
                if (!preview) return null;
                return (
                  <div className="memeet-modal-preview" title={msg?.text || ""}>
                    {preview}
                  </div>
                );
              })()}

              {checklistActionError ? <div className="memeet-modal-error">{checklistActionError}</div> : null}
            </div>

            <div className="memeet-modal-actions">
              <button
                className="meet-button ghost"
                type="button"
                onClick={() => closeAddToChecklistPicker()}
                disabled={isChecklistAddInFlight}
              >
                {t("memeetCancel", "Cancel")}
              </button>
              <button
                className="meet-button primary"
                type="button"
                onClick={handleConfirmAddToChecklist}
                disabled={!selectedChecklistWorkspaceId || isChecklistAddInFlight || !canAddToChecklist}
              >
                {isChecklistAddInFlight ? t("memeetAdding", "Adding...") : t("memeetAdd", "Add")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isInviteModalOpen ? (
        <div
          className="memeet-modal-backdrop"
          onMouseDown={() => {
            if (!isInviteSubmitting) closeInviteModal();
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div
            className="memeet-modal memeet-invite-modal"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <form className="memeet-invite-form" onSubmit={handleInviteSubmit}>
              <div className="memeet-invite-header">
                <h3 className="memeet-modal-title">{t("memeetInvitePeople", "Invite People")}</h3>
                <button
                  type="button"
                  className="icon-button"
                  onClick={closeInviteModal}
                  disabled={isInviteSubmitting}
                  title={t("memeetClose", "Close")}
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="room-chip memeet-invite-room-chip" title={t("memeetMeetingLink", "Toplantı linki")}>
                <span className="room-chip-label">{t("memeetMeetingLink", "Meeting Link")}</span>
                <code className="room-chip-code">{sharePath}</code>
                <button className="chip-button" onClick={handleCopyRoomId} title={t("memeetCopy", "Kopyala")} type="button">
                  {isCopied ? <CheckIcon /> : <CopyIcon />}
                </button>
              </div>

              <div className="memeet-invite-sections">
                {isRoomCreator ? (
                <>
                <section className="memeet-invite-section">
                  <div className="memeet-invite-section-title-row">
                    <h4>{t("memeetMyCollaborators", "My Collaborators")}</h4>
                    {selectedCollaboratorId ? (
                      <button
                        type="button"
                        className="memeet-inline-clear-btn"
                        onClick={() => setSelectedCollaboratorId("")}
                        disabled={isInviteSubmitting}
                      >
                        {t("memeetClearSelection", "Clear")}
                      </button>
                    ) : null}
                  </div>

                  {!canLoadCollaborators ? (
                    <p className="memeet-invite-hint">
                      {t("memeetCollaboratorsUnavailable", "Collaborators are not available.")}
                    </p>
                  ) : isCollaboratorsLoading ? (
                    <p className="memeet-invite-hint">{t("memeetLoading", "Loading...")}</p>
                  ) : collaborators.length === 0 ? (
                    <p className="memeet-invite-hint">{t("memeetNoCollaborators", "No collaborators found.")}</p>
                  ) : (
                    <>
                      <div className="memeet-invite-search-row">
                        <input
                          type="search"
                          className="memeet-invite-search-input"
                          value={collaboratorSearchQuery}
                          onChange={(e) => setCollaboratorSearchQuery(e.target.value)}
                          placeholder={t("memeetSearchCollaborators", "Search by name or email")}
                          disabled={isInviteSubmitting}
                        />
                        {collaboratorSearchTerm ? (
                          <button
                            type="button"
                            className="memeet-invite-search-clear"
                            onClick={() => setCollaboratorSearchQuery("")}
                            disabled={isInviteSubmitting}
                          >
                            {t("memeetClearSelection", "Clear")}
                          </button>
                        ) : null}
                      </div>

                      {filteredCollaborators.length === 0 ? (
                        <p className="memeet-invite-hint">
                          {t("memeetNoCollaboratorsMatchingSearch", "No collaborators match your search.")}
                        </p>
                      ) : (
                        <div className="memeet-invite-collaborators-list">
                          {filteredCollaborators.map((collaborator) => {
                            const userID = collaborator.id.startsWith("email:") ? "" : collaborator.id;
                            const invitedKey = makeInvitedCheckKey("collab", userID, collaborator.email);
                            const isInvited = Boolean(invitedStatusByKey[invitedKey]);
                            const isChecking = Boolean(isCheckingInvitedByKey[invitedKey]);
                            const isSelected = selectedCollaboratorId === collaborator.id;

                            return (
                              <button
                                key={collaborator.id}
                                type="button"
                                className={`memeet-invite-collaborator-item${isSelected ? " selected" : ""}`}
                                onClick={() => {
                                  setSelectedCollaboratorId(collaborator.id);
                                  setInviteActionError("");
                                }}
                                disabled={isInviteSubmitting}
                              >
                                <span className="memeet-invite-collaborator-main">
                                  <span className="memeet-invite-collaborator-name">{collaborator.name}</span>
                                  {collaborator.email ? (
                                    <span className="memeet-invite-collaborator-email">{collaborator.email}</span>
                                  ) : null}
                                </span>
                                <span className="memeet-invite-status-indicator">
                                  {isChecking ? (
                                    <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                  ) : isInvited ? (
                                    <InvitedCheckIcon />
                                  ) : null}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </section>

                <section className="memeet-invite-section">
                  <h4>{t("memeetInviteByEmail", "Invite by Email")}</h4>
                  <div className="memeet-invite-fields">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => {
                        setInviteEmail(e.target.value);
                        setInviteActionError("");
                      }}
                      disabled={Boolean(selectedCollaboratorId) || isInviteSubmitting}
                      placeholder={t("memeetEmailPlaceholder", "email@example.com")}
                    />
                    <input
                      type="text"
                      value={inviteNameLastname}
                      onChange={(e) => {
                        setInviteNameLastname(e.target.value);
                        setInviteActionError("");
                      }}
                      disabled={Boolean(selectedCollaboratorId) || isInviteSubmitting}
                      placeholder={t("memeetNameLastnamePlaceholder", "Name Lastname")}
                    />
                  </div>
                  <p className="memeet-invite-hint">
                    {t(
                      "memeetInviteInputRuleHint",
                      "Use collaborator selection or email fields. If collaborator is selected, email fields are ignored."
                    )}
                  </p>
                </section>
                </>
                ) : null}

                <section className="memeet-invite-section memeet-invite-section--wide">
                  <h4>{t("memeetInvitedList", "Invited List")}</h4>
                  {!canLoadInvitations ? (
                    <p className="memeet-invite-hint">
                      {t("memeetInvitationsUnavailable", "Invitations are not available.")}
                    </p>
                  ) : isInvitationsLoading ? (
                    <p className="memeet-invite-hint">{t("memeetLoading", "Loading...")}</p>
                  ) : invitations.length === 0 ? (
                    <p className="memeet-invite-hint">{t("memeetNoInvitations", "No invitations yet.")}</p>
                  ) : (
                    <div className="memeet-invited-list">
                      {invitations.map((invitation) => {
                        const invitedKey = makeInvitedCheckKey(
                          "invite",
                          invitation.userID,
                          invitation.email
                        );
                        const isInvited = Boolean(invitedStatusByKey[invitedKey]);
                        const isChecking = Boolean(isCheckingInvitedByKey[invitedKey]);

                        return (
                          <div key={invitation.invitationCode} className="memeet-invited-row">
                            <div className="memeet-invited-main">
                              <span className="memeet-invited-name">
                                {displayInvitationName(invitation)}
                              </span>
                              <span className="memeet-invited-sub">
                                {invitation.email || invitation.userID || invitation.invitationCode}
                              </span>
                            </div>
                            <div className="memeet-invited-actions">
                              <span className="memeet-invite-status-indicator">
                                {isChecking ? (
                                  <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                ) : isInvited ? (
                                  <InvitedCheckIcon />
                                ) : null}
                              </span>
                              {isRoomCreator ? (
                              <button
                                type="button"
                                className="meet-button ghost small"
                                onClick={() => setInvitationToRemove(invitation)}
                                disabled={!canRemoveInvitations || isRemovingInvitation}
                              >
                                {t("memeetCancelInvitation", "Cancel")}
                              </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>

              {isRoomCreator ? inviteActionError ? <div className="memeet-modal-error">{inviteActionError}</div> : null : null}
              {isRoomCreator ? inviteActionSuccess ? <div className="memeet-modal-success">{inviteActionSuccess}</div> : null : null}

              {/* Bildirim kanallari — secim KULLANICIYA ait. Hicbiri secilmezse
                  davet yine olusur, kullanici asagidaki baglantiyi kendi gonderir. */}
              {isRoomCreator ? (
                <div className="memeet-notify-channels">
                  <span className="memeet-notify-label">
                    {t("memeetNotifyVia", "Notify via")}
                  </span>
                  {([
                    ["notification", t("memeetNotifyInApp", "In-app notification")],
                    ["push", t("memeetNotifyPush", "Push notification")],
                    ["mail", t("memeetNotifyMail", "Email")],
                  ] as [string, string][]).map(([key, label]) => (
                    <label key={key} className="memeet-notify-option">
                      <input
                        type="checkbox"
                        checked={(notifyChannels as Record<string, boolean>)[key]}
                        onChange={(e) =>
                          setNotifyChannels((prev) => ({ ...prev, [key]: e.target.checked }))
                        }
                        disabled={isInviteSubmitting}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              ) : null}

              {inviteJoinLink ? (
                <div className="memeet-invite-link">
                  <input
                    className="memeet-invite-link-input"
                    type="text"
                    value={inviteJoinLink}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    className="meet-button ghost"
                    type="button"
                    onClick={() => {
                      try {
                        navigator.clipboard?.writeText(inviteJoinLink);
                        setInviteLinkCopied(true);
                        setTimeout(() => setInviteLinkCopied(false), 1600);
                      } catch {
                        /* pano erisimi yoksa kullanici elle secebilir */
                      }
                    }}
                  >
                    {inviteLinkCopied ? t("copied", "Copied") : t("copy", "Copy")}
                  </button>
                </div>
              ) : null}

              <div className="memeet-modal-actions">
                <button
                  className="meet-button ghost"
                  type="button"
                  onClick={closeInviteModal}
                  disabled={isInviteSubmitting}
                >
                  {t("memeetClose", "Close")}
                </button>
                {isRoomCreator ? (
                <button
                  className="meet-button primary"
                  type="submit"
                  disabled={!canInviteUsers || isInviteSubmitting}
                >
                  {isInviteSubmitting ? t("memeetSendingInvitation", "Sending...") : t("memeetSendInvite", "Send Invite")}
                </button>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {invitationToRemove ? (
        <div
          className="memeet-modal-backdrop"
          onMouseDown={() => {
            if (!isRemovingInvitation) setInvitationToRemove(null);
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="memeet-modal memeet-invite-confirm-modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="memeet-modal-title">{t("memeetCancelInvitation", "Cancel Invitation")}</h3>
            <div className="memeet-modal-body">
              <p className="memeet-invite-hint">
                {t(
                  "memeetCancelInvitationConfirm",
                  "Are you sure you want to cancel this invitation?"
                )}
              </p>
              <div className="memeet-modal-preview">
                {displayInvitationName(invitationToRemove)}
              </div>
            </div>
            <div className="memeet-modal-actions">
              <button
                className="meet-button ghost"
                type="button"
                onClick={() => setInvitationToRemove(null)}
                disabled={isRemovingInvitation}
              >
                {t("memeetClose", "Close")}
              </button>
              <button
                className="meet-button danger"
                type="button"
                onClick={() => void handleConfirmRemoveInvitation()}
                disabled={isRemovingInvitation || !canRemoveInvitations}
              >
                {isRemovingInvitation ? t("memeetRemoving", "Removing...") : t("memeetConfirm", "Confirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {lightboxImage && createPortal(
        <div className="memeet-lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <button className="memeet-lightbox-close" onClick={() => setLightboxImage(null)}>
            <CloseIcon />
          </button>
          <img src={lightboxImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>,
        document.body
      )}
    </div>
  );
}

function getPeerConnectionVideoSenders(pc: RTCPeerConnection): RTCRtpSender[] {
  const senders: RTCRtpSender[] = [];
  const addUnique = (sender: RTCRtpSender | null | undefined) => {
    if (!sender) return;
    if (!senders.includes(sender)) senders.push(sender);
  };

  pc.getSenders().forEach((sender) => {
    if (sender.track?.kind === "video") addUnique(sender);
  });

  const transceivers = typeof pc.getTransceivers === "function" ? pc.getTransceivers() : [];
  transceivers.forEach((transceiver) => {
    const isVideoTransceiver =
      transceiver.sender?.track?.kind === "video" || transceiver.receiver?.track?.kind === "video";
    if (!isVideoTransceiver) return;

    const preferredDirection = String(transceiver.direction || "").toLowerCase();
    const negotiatedDirection = String(transceiver.currentDirection || "").toLowerCase();
    // currentDirection can temporarily be recvonly/inactive while slot is still intended for sending.
    const canSendByPreference = preferredDirection === "sendrecv" || preferredDirection === "sendonly";
    const canSendByNegotiation = negotiatedDirection === "sendrecv" || negotiatedDirection === "sendonly";
    if (!canSendByPreference && !canSendByNegotiation) return;

    addUnique(transceiver.sender);
  });

  return senders;
}

function ensurePeerConnectionVideoSenderCount(pc: RTCPeerConnection, minimumCount: number) {
  if (minimumCount <= 0) return;

  let senders = getPeerConnectionVideoSenders(pc);
  while (senders.length < minimumCount) {
    try {
      pc.addTransceiver("video", { direction: "sendrecv" });
    } catch (error) {
      console.warn("Failed to add sendrecv video transceiver:", error);
      break;
    }
    senders = getPeerConnectionVideoSenders(pc);
  }
}

function getSenderMid(pc: RTCPeerConnection, sender: RTCRtpSender | null | undefined): string | null {
  if (!sender || typeof pc.getTransceivers !== "function") return null;
  const transceiver = pc.getTransceivers().find((item) => item.sender === sender);
  return transceiver?.mid ?? null;
}

function resolveVideoSenderSlots(
  pc: RTCPeerConnection,
  localCameraTrack: MediaStreamTrack | null | undefined,
  preferredSlots?: VideoSenderSlots | null,
  activeScreenTrack?: MediaStreamTrack | null
): VideoSenderSlots {
  const senders = getPeerConnectionVideoSenders(pc);
  if (senders.length === 0) {
    return { cameraSender: null, screenSender: null };
  }

  const localCameraTrackId = localCameraTrack?.id || null;
  const activeScreenTrackId = activeScreenTrack?.id || null;
  const isPreferredValid = (sender: RTCRtpSender | null | undefined) => Boolean(sender && senders.includes(sender));
  const preferredCamera = isPreferredValid(preferredSlots?.cameraSender) ? preferredSlots?.cameraSender ?? null : null;
  const preferredScreen = isPreferredValid(preferredSlots?.screenSender) ? preferredSlots?.screenSender ?? null : null;

  let cameraSender =
    preferredCamera ??
    (localCameraTrackId ? senders.find((sender) => sender.track?.id === localCameraTrackId) : undefined) ??
    senders.find((sender) => sender.track?.kind === "video" && sender.track?.id !== activeScreenTrackId) ??
    (activeScreenTrackId ? senders.find((sender) => sender.track?.id !== activeScreenTrackId) : undefined) ??
    senders[0] ??
    null;

  if (cameraSender && !getSenderMid(pc, cameraSender)) {
    const negotiatedCamera =
      (localCameraTrackId
        ? senders.find((sender) => Boolean(getSenderMid(pc, sender)) && sender.track?.id === localCameraTrackId)
        : undefined) ?? senders.find((sender) => Boolean(getSenderMid(pc, sender)));
    if (negotiatedCamera) {
      cameraSender = negotiatedCamera;
    }
  }

  const otherSenders = senders.filter((sender) => sender !== cameraSender);

  let screenSender =
    (preferredScreen && preferredScreen !== cameraSender ? preferredScreen : null) ??
    (activeScreenTrackId ? otherSenders.find((sender) => sender.track?.id === activeScreenTrackId) : undefined) ??
    otherSenders.find((sender) => !sender.track) ??
    (localCameraTrackId
      ? otherSenders.find((sender) => sender.track?.id !== localCameraTrackId)
      : otherSenders[0]) ??
    otherSenders[0] ??
    null;

  if (screenSender && !getSenderMid(pc, screenSender)) {
    const negotiatedScreen = otherSenders.find((sender) => Boolean(getSenderMid(pc, sender)));
    if (negotiatedScreen) {
      screenSender = negotiatedScreen;
    }
  }

  return { cameraSender, screenSender };
}

function describeMediaStreamTracks(stream: MediaStream | null | undefined) {
  if (!stream) {
    return { audio: [] as string[], video: [] as string[] };
  }

  return {
    audio: stream.getAudioTracks().map((track) => `${track.id}:${track.enabled ? "on" : "off"}:${track.readyState}`),
    video: stream.getVideoTracks().map((track) => `${track.id}:${track.enabled ? "on" : "off"}:${track.readyState}`),
  };
}

function describePeerVideoSenders(pc: RTCPeerConnection, localCameraTrack?: MediaStreamTrack | null) {
  const localCameraTrackId = localCameraTrack?.id || null;
  const { cameraSender, screenSender } = resolveVideoSenderSlots(pc, localCameraTrack);

  return getPeerConnectionVideoSenders(pc).map((sender, index) => ({
    index,
    trackId: sender.track?.id ?? null,
    enabled: sender.track?.enabled ?? null,
    readyState: sender.track?.readyState ?? null,
    slot:
      sender === cameraSender
        ? "camera"
        : sender === screenSender
          ? "screen"
          : "other",
    matchesLocalCameraTrack: Boolean(localCameraTrackId && sender.track?.id === localCameraTrackId),
  }));
}

function pickLikelyScreenTrack(videoTracks: MediaStreamTrack[]): MediaStreamTrack | null {
  if (!Array.isArray(videoTracks) || videoTracks.length === 0) return null;

  const keywordPattern =
    /\b(screen|display|window|monitor|tab|capture|share|ekran|paylas|paylasim|paylaş|paylaşım)\b/i;
  const byLabel = videoTracks.find((track) => keywordPattern.test(String(track.label || "")));
  if (byLabel) return byLabel;

  const liveTracks = videoTracks.filter((track) => track.readyState === "live");
  if (liveTracks.length === 1) return liveTracks[0];

  return videoTracks[videoTracks.length - 1] || null;
}

function AvatarCircle({ name, avatarUrl }: { name: string; avatarUrl?: string }) {
  if (avatarUrl) {
    return <img className="avatar-image" src={avatarUrl} alt={name} />;
  }

  return <span className="avatar-initials">{getInitials(name)}</span>;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]).join("");
  return initials.toUpperCase() || "U";
}

function shouldCreateOffer(localUserId: string | null, remoteUserId: string): boolean {
  if (!localUserId) return false;
  return localUserId.localeCompare(remoteUserId) < 0;
}

function parseJoinRoomRosterAck(data: unknown): Array<{ odaHakları: string; nickname: string }> {
  const obj = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
  const rawList = Array.isArray(data)
    ? data
    : obj && Array.isArray(obj.users)
      ? obj.users
      : obj && Array.isArray(obj.participants)
        ? obj.participants
        : obj && Array.isArray(obj.members)
          ? obj.members
          : [];

  return rawList
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const item = entry as Record<string, unknown>;
      const odaHakları = String(item.odaHakları ?? item.id ?? item.userId ?? item.socketId ?? "").trim();
      const nickname = String(item.nickname ?? item.name ?? item.displayName ?? "").trim();
      if (!odaHakları || !nickname) return null;
      return { odaHakları, nickname };
    })
    .filter((u): u is { odaHakları: string; nickname: string } => Boolean(u));
}

const IDENTITY_MARKER = "|memeet:v1:";

function encodeIdentityNickname(displayName: string, avatarUrl?: string, lodosUserId?: string): string {
  const safeName = (displayName || "").trim() || "User";
  const safeAvatar = (avatarUrl || "").trim();
  const safeUserId = String(lodosUserId || "").trim();

  const payloadObj: { a?: string; u?: string } = {};
  if (safeAvatar) payloadObj.a = safeAvatar;
  if (safeUserId) payloadObj.u = safeUserId;
  if (!payloadObj.a && !payloadObj.u) return safeName;

  const payload = JSON.stringify(payloadObj);
  return `${safeName} ${IDENTITY_MARKER}${base64UrlEncode(payload)}`;
}

function parseIdentityNickname(rawNickname: string): { displayName: string; avatarUrl?: string; lodosUserId?: string } {
  const raw = (rawNickname || "").trim();
  const idx = raw.indexOf(IDENTITY_MARKER);
  if (idx === -1) return { displayName: raw || "User" };

  const displayName = raw.slice(0, idx).trim() || "User";
  const encoded = raw.slice(idx + IDENTITY_MARKER.length).trim();

  try {
    const decoded = base64UrlDecode(encoded);
    const parsed = JSON.parse(decoded) as { a?: unknown; u?: unknown };
    const avatar = typeof parsed?.a === "string" ? parsed.a : undefined;
    const userId = typeof parsed?.u === "string" ? parsed.u : undefined;
    return { displayName, avatarUrl: avatar, lodosUserId: userId };
  } catch {
    return { displayName };
  }
}

function base64UrlEncode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(input: string): string {
  let base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4;
  if (pad) base64 += "=".repeat(4 - pad);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

function makeChatId(): string {
  const cryptoObj = (globalThis as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof cryptoObj?.randomUUID === "function") {
    try {
      return cryptoObj.randomUUID();
    } catch {
      // ignore
    }
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function truncateChatPreview(text: string, maxLength = 72): string {
  const compact = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function truncateChatActionToken(text: string, maxChars = 6): string {
  const compact = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!compact) return "";
  const chars = Array.from(compact);
  if (chars.length <= maxChars) return compact;
  return `${chars.slice(0, maxChars).join("")}...`;
}

function decodeRtcData(data: unknown): string | null {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
  if (ArrayBuffer.isView(data)) return new TextDecoder().decode(data);
  return null;
}

function parseMediaStateSignal(data: unknown): { audioEnabled: boolean; videoEnabled: boolean } | null {
  let value: unknown = data;

  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== "object") return null;

  const obj = value as Record<string, unknown>;
  const audioEnabled = obj.audioEnabled;
  const videoEnabled = obj.videoEnabled;

  if (typeof audioEnabled !== "boolean" || typeof videoEnabled !== "boolean") return null;
  return { audioEnabled, videoEnabled };
}

function parseScreenShareStateSignal(data: unknown): { enabled: boolean; mid?: string } | null {
  let value: unknown = data;

  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== "object") return null;

  const obj = value as Record<string, unknown>;
  const enabled = obj.enabled ?? obj.isScreenSharing ?? obj.screenSharing;
  if (typeof enabled !== "boolean") return null;

  const mid = typeof obj.mid === "string" ? obj.mid.trim() : "";
  if (mid) return { enabled, mid };
  return { enabled };
}

function parseHandStateSignal(data: unknown): { raised: boolean } | null {
  let value: unknown = data;

  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== "object") return null;

  const obj = value as Record<string, unknown>;
  const raised = obj.raised ?? obj.isHandRaised ?? obj.handRaised;
  if (typeof raised !== "boolean") return null;

  return { raised };
}

function parseChatWireChecklistAddedV1(raw: string): ChatWireChecklistAddedV1 | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "chat-message" || obj.v !== 1) return null;

    const meta = obj.meta && typeof obj.meta === "object" ? (obj.meta as Record<string, unknown>) : null;
    if (!meta || meta.kind !== "checklist-added") return null;

    const messageId = typeof meta.messageId === "string" ? meta.messageId : null;
    const workspaceId = typeof meta.workspaceId === "string" ? meta.workspaceId : null;
    const checklistName = typeof meta.checklistName === "string" ? meta.checklistName : null;
    if (!messageId || !workspaceId || !checklistName) return null;

    const ts = typeof obj.ts === "number" ? obj.ts : Date.now();
    const checklistId = typeof meta.checklistId === "string" ? meta.checklistId : undefined;
    const byUserId = typeof meta.byUserId === "string" ? meta.byUserId : undefined;
    const byName = typeof meta.byName === "string" ? meta.byName : undefined;
    const preview = typeof meta.preview === "string" ? meta.preview : undefined;

    return {
      v: 1,
      type: "chat-checklist-added",
      ts,
      messageId,
      workspaceId,
      checklistName,
      checklistId,
      byUserId,
      byName,
      preview,
    };
  } catch {
    return null;
  }
}

function parseSoundboardWireMessageV1(raw: string): SoundboardWireMessageV1 | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "soundboard-play" || obj.v !== 1) return null;

    const soundId = typeof obj.soundId === "string" ? obj.soundId : null;
    if (!soundId) return null;

    return {
      v: 1,
      type: "soundboard-play",
      soundId,
      fromName: typeof obj.fromName === "string" ? obj.fromName : "",
      ts: typeof obj.ts === "number" ? obj.ts : Date.now(),
    };
  } catch {
    return null;
  }
}

function parseChatWireMessageV1(raw: string): { id: string; ts: number; text: string; fromName?: string; fromAvatarUrl?: string; attachment?: ChatAttachment } | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "chat-message" || obj.v !== 1) return null;

    const id = typeof obj.id === "string" ? obj.id : null;
    const ts = typeof obj.ts === "number" ? obj.ts : null;
    const text = typeof obj.text === "string" ? obj.text : "";
    if (!id) return null;

    const from = obj.from && typeof obj.from === "object" ? (obj.from as Record<string, unknown>) : null;
    const fromName = from && typeof from.name === "string" ? from.name : undefined;
    const fromAvatarUrl = from && typeof from.avatarUrl === "string" ? from.avatarUrl : undefined;

    let attachment: ChatAttachment | undefined;
    if (obj.attachment && typeof obj.attachment === "object") {
      const att = obj.attachment as Record<string, unknown>;
      if (
        typeof att.type === "string" &&
        typeof att.name === "string" &&
        typeof att.data === "string" &&
        typeof att.size === "number"
      ) {
        attachment = {
          type: att.type,
          name: att.name,
          data: att.data,
          size: att.size,
        };
      }
    }

    return { id, ts: ts ?? Date.now(), text, fromName, fromAvatarUrl, attachment };
  } catch {
    return null;
  }
}

function AttachmentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function VideoOffIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function HandRaiseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 11V6a2 2 0 0 0-4 0v5" />
      <path d="M14 10V4a2 2 0 0 0-4 0v6" />
      <path d="M10 10V5a2 2 0 0 0-4 0v5" />
      <path d="M6 12v-1a2 2 0 0 0-4 0v2a10 10 0 0 0 10 10h2a8 8 0 0 0 8-8v-3a2 2 0 0 0-4 0v1" />
    </svg>
  );
}

function ScreenShareIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function PiPIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <rect x="12" y="9" width="8" height="6" rx="1" ry="1" fill="currentColor" />
    </svg>
  );
}

function PhoneOffIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function InviteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  );
}

function InvitedCheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.8 2.8L16.5 9.8" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChecklistAddIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="4" cy="6" r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h7" />
      <path d="M18 17h5" />
      <path d="M20.5 14.5v5" />
    </svg>
  );
}

function NotesAddIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12V8z" />
      <path d="M14 2v6h6" />
      <path d="M18 17h5" />
      <path d="M20.5 14.5v5" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SoundboardToggleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

function SoundboardIcon({ icon }: { icon: string }) {
  switch (icon) {
    case "clap":
      return <span className="soundboard-emoji">👏</span>;
    case "clap-single":
      return <span className="soundboard-emoji">👋</span>;
    case "ovation":
      return <span className="soundboard-emoji">🎉</span>;
    case "laugh":
      return <span className="soundboard-emoji">😂</span>;
    case "mock":
      return <span className="soundboard-emoji">🤣</span>;
    case "news":
      return <span className="soundboard-emoji">📰</span>;
    case "cricket":
      return <span className="soundboard-emoji">🦗</span>;
    case "ahh":
      return <span className="soundboard-emoji">😮</span>;
    case "choir":
      return <span className="soundboard-emoji">😇</span>;
    case "man-ahh":
      return <span className="soundboard-emoji">🗣️</span>;
    case "sad":
      return <span className="soundboard-emoji">😢</span>;
    case "wow":
      return <span className="soundboard-emoji">🤩</span>;
    default:
      return <span className="soundboard-emoji">🔊</span>;
  }
}
